import i18n from "i18next";

import {
  type Bank,
  bankEnum,
  type PaymentProvider,
  paymentProviderEnum,
} from "@dfp/bw-isomorphic-enums";

import type { BwCashflowModel } from "@dfp/bw-sdk-bw-cashflow";
// ── Real response-shape anchors ───────────────────────────────────────────────
// Demo builders are typed against the GENERATED SDK models (the exact shapes the
// real backend returns) via DeepPartial. This lets the mocks stay lean (only the
// fields the FE actually reads) while the compiler still rejects wrong field
// names and invalid enum/literal values — i.e. it turns "hallucinated" data into
// compile errors instead of runtime surprises.
import type { DmbBankaccountsModel } from "@dfp/bw-sdk-dmb-bankaccounts";
import type { DmbTransactionsModel } from "@dfp/bw-sdk-dmb-transactions";

import { accountNameLabel } from "./demoBankConnect.utils.ts";
import { ACCOUNTING_ATTRIBUTES_HANDLERS } from "./accountingAttributesMock.ts";
import {
  companyFromSettingsDocId,
  getCompanySettings,
  updateCompanySettings,
} from "./demoCompanySettings.ts";
import {
  archiveAccounts,
  archivedAt,
  isArchivedAccountId,
  isConnectionRefreshed,
  markConnectionsRefreshed,
  rememberReconnect,
  unarchiveAccounts,
} from "./demoArchiveStore.ts";
import {
  apActivateContract,
  apContractList,
  apContractTerms,
  withApContracts,
} from "./apActivationDemo.ts";
import { buildEntitlementTemplateList } from "./seed/entitlementTemplates.ts";
import { PAYABLES_HANDLERS } from "./payablesMock.ts";
import {
  type DemoConnectedAccount,
  commitAllPendingConnections,
  getAddedAccounts,
  updatePendingAccount,
  getPendingConnection,
} from "./demoConnectionStore.ts";
import {
  contractsCreateDocument,
  contractsCreateEntity,
  contractsDeleteEntity,
  contractsDownloadAttachment,
  contractsGetDocumentList,
  contractsGetSections,
  contractsUpdateEntity,
  contractsUploadAttachment,
} from "./demoContracts.ts";
import { getDemoLanguage, setDemoLanguage } from "./demoLanguage.ts";
import { isDemoOnboardingActive } from "./demoOnboarding.ts";
import { makeInitialsAvatar } from "./seed/avatars.ts";
import {
  DEMO_ACCOUNTS,
  DEMO_ACCOUNT_BY_ID,
  DEMO_BANKS,
  DEMO_CLIENT_ROLE_BY_COMPANY,
  DEMO_COMPANIES,
  DEMO_COMPANY_BY_ID,
  DEMO_CONTACTS,
  DEMO_LIVE_PERSON_ID,
  DEMO_REGISTERED_ROLE_ID,
  DEMO_TEAM,
  DEMO_TRANSACTIONS,
  DEMO_USER,
  DEMO_USER_ID,
  DEMO_USER_USERNAME,
  demoOnboardingUserContextResponse,
  demoUserContextResponse,
  type DemoAccount,
  type DemoAccountCurrency,
  type DemoTeamMember,
} from "./seed/index.ts";

type Json = unknown;
type MockRequest = {
  appName: string;
  operation: string;
  body: Record<string, unknown>;
  method: string;
};
type Handler = (req: MockRequest) => Json;

/**
 * Recursively optional. Wrapping a generated response type in DeepPartial lets a
 * demo builder return only the subset of fields the UI consumes, while the
 * compiler still validates that every field NAME and enum/literal VALUE that IS
 * present matches the real backend contract.
 */
type DeepPartial<T> = T extends (infer U)[]
  ? DeepPartial<U>[]
  : T extends object
    ? { [K in keyof T]?: DeepPartial<T[K]> }
    : T;

/** Narrow a getDocumentList response union to a single documentType variant. */
type DocOf<TResponse, TDocType extends string> = Extract<
  TResponse extends { documentList: (infer TItem)[] } ? TItem : never,
  { documentType: TDocType }
>;

type LiveBankAccountDoc = DocOf<
  DmbBankaccountsModel["getDocumentList"]["response"],
  "LiveBankAccount"
>;
type LiveTransactionDoc = DocOf<
  DmbTransactionsModel["getDocumentList"]["response"],
  "LiveTransaction"
>;
type HistoricalBalanceResponse =
  BwCashflowModel["getHistoricalBalance"]["response"];

const HANDLERS: Record<string, Handler> = {
  // bw-party
  "bw-party/getUserContext": () =>
    isDemoOnboardingActive()
      ? demoOnboardingUserContextResponse
      : withApContracts(demoUserContextResponse),
  "bw-party/getUserContextOnBehalfOf": () =>
    isDemoOnboardingActive()
      ? demoOnboardingUserContextResponse
      : demoUserContextResponse,
  "bw-party/getUserLastLogin": () => ({
    lastLoginAt: "2026-05-20T18:42:11.000Z",
  }),
  "bw-party/getUserConsents": () => [],
  "bw-party/authenticateCaller": () => ({ authenticated: true }),
  "bw-party/getInvitationLinks": () => ({ links: [] }),
  "bw-party/getDocumentList": (req) => partyGetDocumentList(req),
  "bw-party/readEntities": (req) => partyReadEntities(req),
  "bw-party/getEntitiesValidationRules": () => ({ rules: [] }),

  // bw-user-preferences
  "bw-user-preferences/getFEAppPartyRoleRelations": () =>
    buildPartyRoleRelations(),
  "bw-user-preferences/readEntities": () => readEntitiesUserPrefs(),
  "bw-user-preferences/getDocumentList": () => emptyDocList(),
  "bw-user-preferences/createEntity": (req) => saveDemoDashboardPrefs(req),
  "bw-user-preferences/updateEntity": (req) => saveDemoDashboardPrefs(req),

  // bw-clientsettings + bw-set-entitle + bw-contracts
  "bw-clientsettings/getDocumentList": (req) => buildClientSettingsList(req),
  "bw-clientsettings/readEntities": (req) => buildClientSettingsList(req),
  "bw-clientsettings/createDocument": (req) => clientSettingsCreateDocument(req),
  "bw-set-entitle/getDocumentList": () => buildEntitlementTemplateList(),
  "bw-set-entitle/readEntities": () => ({ entities: [] }),
  "bw-contracts/getDocumentList": (req) =>
    apContractList(req.body) ?? contractsGetDocumentList(req.body),
  "bw-contracts/readEntities": () => ({ entities: [] }),
  "bw-contracts/getAvailableProducts": () => buildAvailableProducts(),
  "bw-contracts/getMyProducts": () => buildMyProducts(),
  "bw-contracts/getContractSections": (req) => contractsGetSections(req.body),
  "bw-contracts/createDocument": (req) =>
    apActivateContract(req.body) ?? contractsCreateDocument(req.body),
  "bw-contracts/createEntity": (req) => contractsCreateEntity(req.body),
  "bw-contracts/updateEntity": (req) => contractsUpdateEntity(req.body),
  "bw-contracts/deleteEntity": (req) => contractsDeleteEntity(req.body),
  "bw-contracts/uploadAttachment": (req) => contractsUploadAttachment(req.body),
  "bw-contracts/downloadAttachment": (req) =>
    contractsDownloadAttachment(req.body),
  "bw-contracts/getCurrentContractTermsVersion": (req) =>
    apContractTerms(req.body) ?? buildContractTerms(),
  "bw-contracts/getCurrentPricelistVersion": () => buildPricelist(),
  // Validation rules — empty so nothing blocks the BusinessInsurance wizard.
  "bw-contracts/getEntitiesValidationRules": (req) =>
    buildEntitiesValidationRules(req),

  // bw-ext-direct-insurance — BusinessInsurance quote/calculation + lookups.
  "bw-ext-direct-insurance/getCalculation": (req) =>
    buildInsuranceCalculation(req),
  "bw-ext-direct-insurance/getCadastralArea": () => buildCadastralArea(),
  "bw-ext-direct-insurance/getHomeRiskZone": () => buildHomeRiskZone(),

  // fx-registers — company lookup for the "Add company" flow.
  "fx-registers/getCompanyList": (req) => buildCompanyLookup(req),

  // bw-product-catalogue
  "bw-product-catalogue/getDocumentList": () => buildProductCatalogue(),
  "bw-product-catalogue/readEntities": () => buildProductCatalogue(),

  // bw-exchange-rate
  "bw-exchange-rate/getCurrentRates": () => buildExchangeRates(),
  "bw-exchange-rate/getDocumentList": () => emptyDocList(),

  // dmb-bankaccounts
  "dmb-bankaccounts/getDocumentList": (req) => buildAccountList(req),
  "dmb-bankaccounts/readEntities": (req) => buildAccountList(req),
  "dmb-bankaccounts/listClientBankAccounts": () => buildClientCompanyList(),
  // Connect-bank flow (demo)
  "dmb-bankaccounts/initAddBankAccounts": (req) =>
    buildInitAddBankAccounts(req),
  "dmb-bankaccounts/createDocument": (req) =>
    archiveBankAccountsRequest(req) ?? buildConnectClientAccounts(req),
  "dmb-bankaccounts/createEntity": (req) => authorizeConnectAccounts(req),
  "dmb-bankaccounts/reconnect": (req) => buildReconnect(req),
  "dmb-bankaccounts/updateEntity": (req) => updateBankAccountAccess(req),

  // dmb-transactions
  "dmb-transactions/getDocumentList": (req) => buildTransactionList(req),
  "dmb-transactions/readEntities": (req) => buildTransactionEntity(req),
  "dmb-transactions/getTransactionSums": (req) => buildTransactionSums(req),

  // dmb-counterparties
  "dmb-counterparties/getDocumentList": (req) => buildCounterpartyList(req),
  "dmb-counterparties/readEntities": (req) => buildCounterpartyList(req),

  // dmb-spaces
  "dmb-spaces/getDocumentList": () => emptyDocList(),

  // bw-cashflow
  "bw-cashflow/getHistoricalBalance": (req) => buildHistoricalBalance(req),
  "bw-cashflow/getCashflowSummary": () => buildCashflowSummary(),
  "bw-cashflow/getForecast": (req) => buildCashflowForecast(req),
  "bw-cashflow/getCashFlowInsightsWidgetData": (req) =>
    buildCashFlowInsightsWidget(req),
  "bw-cashflow/getCategoryBreakdown": () => buildCategoryBreakdown(),
  "bw-cashflow/getCashFlowAndBalanceStates": (req) =>
    buildCashFlowAndBalanceStates(req),
  "bw-cashflow/getDocumentList": () => emptyDocList(),

  // bw-invoices

  // bw-messaging / bw-support / bw-gateway / extensions
  "bw-messaging/getDocumentList": () => emptyDocList(),
  "bw-support/getDocumentList": () => emptyDocList(),
  "bw-gateway/getDocumentList": () => emptyDocList(),
  "bw-ext-saltedge/getDocumentList": () => emptyDocList(),
  "bw-ext-tapix/getDocumentList": () => emptyDocList(),
  "bw-ext-merk/getDocumentList": () => emptyDocList(),
  "bw-ext-dfp/getDocumentList": () => emptyDocList(),
  "bw-ext-flowpay/getDocumentList": () => emptyDocList(),
  // FlowpayCredit wizard — the external embed is gated off in demo mode (see
  // FlowpayCreditDataSubmissionPage), but these endpoints return benign canned
  // data in case any code path reaches them.
  "bw-ext-flowpay/signPayload": () => ({ signature: "demo-signature" }),
  "bw-ext-flowpay/serviceActivation": () => ({
    flowpayCustomerId: "demo-flowpay-customer-1",
    state: "ACTIVE",
  }),
  "bw-ext-flowpay/pushPsd2Data": () => ({ transactionsPushed: 0 }),
  "bw-ext-direct-insurance/getDocumentList": () => emptyDocList(),

  // dfx-ext-finbricks
  "dfx-ext-finbricks/getAccounts": () => ({ accounts: DEMO_ACCOUNTS }),
  "dfx-ext-finbricks/getAccountTransactions": () => ({
    transactions: DEMO_TRANSACTIONS,
  }),
  "dfx-ext-finbricks/getDocumentList": () => emptyDocList(),

  // dfx-* + dia-*
  "dfx-balances/getDocumentList": () => buildBalances(),
  "dfx-counterparties/getDocumentList": (req) => buildCounterpartyList(req),
  "dfx-transactions/getDocumentList": (req) => buildTransactionList(req),
  "dfx-statements/getDocumentList": () => emptyDocList(),
  "dfx-ownbankaccounts/getDocumentList": (req) => buildAccountList(req),
  "dfx-ocr/getDocumentList": () => emptyDocList(),
  "dfx-pricing/getDocumentList": () => emptyDocList(),
  "dfx-business-events/getDocumentList": () => emptyDocList(),
  "dfx-accmovements/getDocumentList": () => emptyDocList(),
  "dfx-clientsettings/getDocumentList": () => emptyDocList(),
  "dfx-contracts/getDocumentList": () => emptyDocList(),
  "dfx-gl/getDocumentList": () => emptyDocList(),
  "fx-payment-cards/getDocumentList": () => buildCards(),
  "fx-payment-cards/readEntities": () => buildCards(),
  "fx-exchange-rate/getCurrentRates": () => buildExchangeRates(),
  "dia-party/getDocumentList": () => emptyDocList(),
  "dia-messaging/getDocumentList": () => emptyDocList(),
  "dia-set-entitle/getDocumentList": () => emptyDocList(),
  "dia-user-preferences/getDocumentList": () => emptyDocList(),
  "dia-pdf-maker/getDocumentList": () => emptyDocList(),
  // Accounts payable + approvals: stateful backend in payablesMock.ts.
  ...(PAYABLES_HANDLERS as Record<string, Handler>),
  ...(ACCOUNTING_ATTRIBUTES_HANDLERS as Record<string, Handler>),
};

// ────────────────────────────────────────────────────────────────────────────
// Help-center FAQs — the real app loads these from a public Strapi
// (VITE_FAQ_STRAPI_URL). The demo URL is fictional, so serve canned items in
// the exact Strapi list-response shape (answer = Strapi blocks format).
// ────────────────────────────────────────────────────────────────────────────

function strapiParagraph(text: string) {
  return [{ type: "paragraph", children: [{ type: "text", text }] }];
}

const DEMO_FAQS: Record<string, { question: string; answer: string }[]> = {
  cs: [
    {
      question: "Jak připojím bankovní účet?",
      answer:
        "Na přehledu klikněte na Přidat účet, vyberte svou banku a potvrďte připojení v jejím internetovém bankovnictví. Připojení probíhá přes PSD2 rozhraní banky a Fidoo nikdy nevidí vaše přihlašovací údaje.",
    },
    {
      question: "Jak často se aktualizují zůstatky a transakce?",
      answer:
        "Zůstatky a transakce se obnovují automaticky několikrát denně. Aktualizaci můžete kdykoli vyvolat i ručně na stránce Účty.",
    },
    {
      question: "Co znamená upozornění na vypršení připojení?",
      answer:
        "Banky vyžadují obnovu souhlasu s přístupem k účtu zpravidla každých 90 dní. Jakmile se blíží konec platnosti, zobrazíme upozornění a připojení jedním kliknutím obnovíte.",
    },
    {
      question: "Jak přidám další společnost?",
      answer:
        "V levém menu klikněte na Přidat a zvolte Přidat společnost. Po vyplnění IČ se údaje předvyplní z obchodního rejstříku.",
    },
    {
      question: "Jak pozvu kolegy do Fidoo?",
      answer:
        "Na přehledu klikněte na Pozvat, zadejte e-mail kolegy a vyberte roli. Pozvánka dorazí e-mailem a platí 7 dní.",
    },
  ],
  en: [
    {
      question: "How do I connect a bank account?",
      answer:
        "On the dashboard click Add account, pick your bank and confirm the connection in its internet banking. The connection uses the bank's PSD2 interface and Fidoo never sees your login credentials.",
    },
    {
      question: "How often are balances and transactions updated?",
      answer:
        "Balances and transactions refresh automatically several times a day. You can also trigger a manual refresh any time on the Accounts page.",
    },
    {
      question: "What does the connection-expiry warning mean?",
      answer:
        "Banks require the account-access consent to be renewed, typically every 90 days. When the expiry approaches we show a warning and you can renew the connection with one click.",
    },
    {
      question: "How do I add another company?",
      answer:
        "Click Add in the left menu and choose Add company. After entering the company ID the details are prefilled from the business register.",
    },
    {
      question: "How do I invite colleagues to Fidoo?",
      answer:
        "Click Invite on the dashboard, enter your colleague's e-mail and pick a role. The invitation arrives by e-mail and is valid for 7 days.",
    },
  ],
};

function buildFaqListResponse(locale: string) {
  const items = DEMO_FAQS[locale] ?? DEMO_FAQS.cs;
  return {
    data: items.map((item, i) => ({
      id: i + 1,
      documentId: `demo-faq-${locale}-${i + 1}`,
      question: item.question,
      answer: strapiParagraph(item.answer),
      createdAt: "2026-01-05T09:00:00.000Z",
      updatedAt: "2026-01-05T09:00:00.000Z",
      publishedAt: "2026-01-05T09:00:00.000Z",
      locale,
    })),
    meta: {
      pagination: {
        page: 1,
        pageSize: items.length,
        pageCount: 1,
        total: items.length,
      },
    },
  };
}

export function getMockResponse(
  url: string,
  body: Record<string, unknown>,
  method: string,
): Json {
  // FAQ Strapi is an external host, not a Fidoo service URL — short-circuit it
  // before the service-URL parsing below. Match the CONFIGURED URL, not a
  // hardcoded host: on Vercel the dashboard env vars override .env.demo, so
  // VITE_FAQ_STRAPI_URL can point at the real Strapi there.
  const faqStrapiUrl = String(import.meta.env.VITE_FAQ_STRAPI_URL ?? "");
  if (
    (faqStrapiUrl && url.startsWith(faqStrapiUrl)) ||
    url.includes("demo-faq")
  ) {
    return buildFaqListResponse(String(body.locale ?? "cs"));
  }
  const parsed = parseUrl(url);
  if (!parsed) return permissiveDefault(method);
  const key = `${parsed.appName}/${parsed.operation}`;
  const handler = HANDLERS[key];
  if (handler) {
    try {
      return handler({
        appName: parsed.appName,
        operation: parsed.operation,
        body,
        method,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[demo] mock handler ${key} threw:`, err);
      return permissiveDefault(method);
    }
  }
  // eslint-disable-next-line no-console
  console.debug(
    `[demo] no mock for ${parsed.appName}/${parsed.operation}, returning default`,
  );
  return defaultForOperation(parsed.operation, method);
}

function parseUrl(url: string): { appName: string; operation: string } | null {
  // URL formats observed across local and deployed builds:
  //   https://demo-api.fidoo.local/api-bw-party/getUserContext     (.env.demo)
  //   /bw-api/bw-dev-bw-party/getUserContext                       (Vercel proxy + bw-dev env prefix)
  //   /api/bw-party/getUserContext                                 (raw)
  // Take the last two path segments — second-to-last is appName candidate,
  // last is operation — then strip env prefixes so we end up with a canonical
  // service id like `bw-party` regardless of how the call was built.
  const segments = url.split("?")[0].split("/").filter(Boolean);
  if (segments.length < 2) return null;
  const operation = segments[segments.length - 1];
  let candidate = segments[segments.length - 2];

  // Strip leading non-Fidoo prefix: `api-bw-party` → `bw-party`.
  const trailing = candidate.match(
    /(bw-[a-z0-9-]+|dmb-[a-z0-9-]+|dfx-[a-z0-9-]+|dia-[a-z0-9-]+|fx-[a-z0-9-]+)$/,
  );
  if (!trailing) return null;
  candidate = trailing[1];

  // Strip embedded env prefix: `bw-dev-bw-party` → `bw-party`,
  // `dmb-staging-dmb-transactions` → `dmb-transactions`, etc. Loop because
  // a URL could in theory have multiple nested env tokens.
  let prev: string;
  do {
    prev = candidate;
    candidate = candidate.replace(
      /^(bw|dmb|dfx|dia|fx)-(?:dev|staging|test|prod|local|qa|preview)-/,
      "",
    );
  } while (candidate !== prev);

  return { appName: candidate, operation };
}

function defaultForOperation(operation: string, method: string): Json {
  if (/^(getDocumentList|getList|list[A-Z])/.test(operation))
    return emptyDocList();
  if (/^(get|read|list|search|find)/.test(operation)) return emptyReadDefault();
  if (method.toLowerCase() === "get") return emptyReadDefault();
  return { documentId: `demo-${operation}-${Date.now()}`, success: true };
}

function permissiveDefault(method: string): Json {
  return method.toLowerCase() === "get"
    ? emptyReadDefault()
    : { success: true };
}

function emptyReadDefault() {
  return {
    documentList: [],
    pagingResponse: {
      totalItemsCount: 0,
      hasNextPage: false,
      pageNumber: 1,
      pageSize: 20,
    },
    entities: [],
    items: [],
    list: [],
    results: [],
    accounts: [],
    transactions: [],
    counterparties: [],
    contracts: [],
    products: [],
    cards: [],
    invoices: [],
    rates: [],
    notifications: [],
    messages: [],
    summary: { totalItemsCount: 0 },
  };
}

// ── Entitlement tree templates (invite flow: role cards, detail, Porovnat) ──
// Built from the production bw-set-entitle seeds — see seed/entitlementTemplates.ts.

function emptyDocList() {
  return {
    documentList: [],
    pagingResponse: {
      totalItemsCount: 0,
      hasNextPage: false,
      pageNumber: 1,
      pageSize: 20,
    },
  };
}

function paged<T>(items: T[], body: Record<string, unknown>) {
  const paging = (body?.paging as Record<string, unknown> | undefined) ?? {};
  const pageSize = Number(paging.pageSize ?? 20);
  const pageNumber = Number(paging.pageNumber ?? 1);
  const start = (pageNumber - 1) * pageSize;
  const slice = items.slice(start, start + pageSize);
  return {
    documentList: slice,
    pagingResponse: {
      totalItemsCount: items.length,
      hasNextPage: start + pageSize < items.length,
      pageNumber,
      pageSize,
    },
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Accounts
// ────────────────────────────────────────────────────────────────────────────

// ── Bank-account access (Manage access dialog + avatar group) ───────────────
// Production account detail shows who has access to the account and lets a
// bankAccountAdmin manage it. Users come from the document's
// bankAccountUsers / bankAccountAccessSettings entities; toggles are persisted
// in-session so the dialog feels real.
const demoAccessOverrides = new Map<string, Map<string, boolean>>();

function buildBankAccountUsers(a: DemoAccount) {
  const overrides = demoAccessOverrides.get(a.id);
  // Same source as the Team tab: role from DEMO_JOB_TO_RELATION_TYPE, and
  // people without a live role are hidden entirely (see hasLiveRole).
  return DEMO_TEAM.filter(
    (m) =>
      m.memberships.some((ms) => ms.companyId === a.companyId) &&
      hasLiveRole(m),
  ).map((m, i) => {
    const isSelf = m.id === DEMO_USER_ID;
    const role = DEMO_JOB_TO_RELATION_TYPE[m.jobTitle] as
      | "WorkspaceOwner"
      | "FinancialSpecialist"
      | "Member";
    const defaultAccess = isSelf || role === "WorkspaceOwner" || i % 4 !== 3;
    return {
      partyRoleRef: {
        documentType: "LiveUser",
        documentId: m.partyRoleId,
        entityId: m.partyRoleId,
      },
      userBasicInfo: {
        firstName: m.firstName,
        surname: m.surname,
        roleRelationType: role,
      },
      hasAccess:
        isSelf || role === "WorkspaceOwner"
          ? true
          : (overrides?.get(m.partyRoleId) ?? defaultAccess),
    };
  });
}

// Assignment dialog: checkbox (assignmentDetail.status) and company changes
// on the pending connection's accounts.
function updatePendingAssignment(req: MockRequest): boolean {
  const body = (req.body ?? {}) as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  const accounts = body.accounts ?? body.updateEntity?.accounts;
  if (!accounts?.entityId) return false;
  const update = accounts.updateEntity ?? {};
  const status = update.assignmentDetail?.entityData?.status;
  const companyId = update.companyRef?.entityData?.documentId;
  return updatePendingAccount(String(accounts.entityId), {
    selected:
      status === "Assigned" ? true : status === "NotAssigned" ? false : undefined,
    companyId: typeof companyId === "string" ? companyId : undefined,
    companyFromUser: typeof companyId === "string" ? true : undefined,
  });
}

function updateBankAccountAccess(req: MockRequest) {
  if (updatePendingAssignment(req)) return { result: "ok" };
  const ref = req.body.updateDocumentReference as
    | { documentId?: string }
    | undefined;
  const update = req.body.updateEntity as
    | {
        bankAccountAccessSettings?: {
          entityData?: {
            bankAccountUsers?: {
              partyRoleRef?: { documentId?: string };
              hasAccess?: boolean;
            }[];
          };
        };
      }
    | undefined;
  const accountId = ref?.documentId;
  const changed =
    update?.bankAccountAccessSettings?.entityData?.bankAccountUsers ?? [];
  if (accountId && changed.length) {
    const overrides =
      demoAccessOverrides.get(accountId) ?? new Map<string, boolean>();
    for (const u of changed) {
      if (u.partyRoleRef?.documentId) {
        overrides.set(u.partyRoleRef.documentId, u.hasAccess === true);
      }
    }
    demoAccessOverrides.set(accountId, overrides);
  }
  return { result: "ok" };
}

function accountDoc(a: DemoAccount): DeepPartial<LiveBankAccountDoc> {
  const bank = DEMO_BANKS[a.bankKey];
  const company = DEMO_COMPANY_BY_ID[a.companyId];
  return {
    documentHeader: {
      id: a.id,
      documentType: "LiveBankAccount",
      _ts: Math.floor(Date.now() / 1000),
      _etag: '"demo-etag"',
    },
    bankAccount: {
      entityData: {
        name: a.name,
        // Production formats the account number via displayBankFormat —
        // without it the number renders empty (e.g. Contacts table).
        displayBankFormat: "ibanFormat",
        ibanFormat: { iban: a.iban, bic: bank.bic },
        bbanFormat: {
          accountNo: a.accountNumber,
          bankCode: bank.bic.slice(0, 4),
        },
        additionalBankInfo: { bankName: bank.name },
        bank: bankForBic(bank.bic),
        currency: a.currency,
        ownersNames: [company.name],
      },
    },
    bankAccountUsers: {
      entityData: { bankAccountUsers: buildBankAccountUsers(a) },
    },
    bankAccountAccessSettings: {
      entityData: { bankAccountUsers: buildBankAccountUsers(a) },
    },
    bankAccountBalance: {
      entityData: {
        // Top-level fields read by the cockpit customize-accounts transform.
        currentBalance: a.balance,
        currency: a.currency,
        originalAmount: {
          amount: a.balance,
          currency: a.currency,
          // Archive cutoff (getArchiveCutoffDate) = last balance update.
          timestamp: archivedAt(a.id) ?? a.lastUpdate,
        },
        amounts: {
          amountEUR: {
            amount:
              a.currency === "EUR"
                ? a.balance
                : Math.round(a.balance * rateToEur(a.currency)),
            currency: "EUR",
          },
        },
        balanceDateTime: a.lastUpdate,
      },
    },
    partyRoleRef: {
      entityData: {
        documentType: "LiveCompany",
        documentId: company.id,
        entityId: company.id,
      },
    },
    partyRoleAdditionalInfo: {
      entityData: {
        roleName: company.name,
      },
    },
    clientCustomInfo: {
      entityData: { bankAccountName: a.name },
    },
    connectionInformation: {
      entityData: {
        ...(isExpiredDemoConnection(a.id)
          ? {
              status: "EXPIRED" as const,
              daysToExpire: -3,
              validTo: new Date(Date.now() - 3 * 86_400_000).toISOString(),
            }
          : {
              status: "CONNECTED" as const,
              daysToExpire: 180,
              validTo: "2026-11-17T00:00:00.000Z",
            }),
        paymentProvider: paymentProviderForBic(DEMO_BANKS[a.bankKey]?.bic),
      },
    },
    docEntityStateFlag: {
      entityData: { isArchived: isArchivedAccountId(a.id) },
    },
    ...(hasIncompleteDemoHistory(a.id) && {
      lastRefreshInfo: {
        entityData: {
          status: "SUCCESS" as const,
          lastSuccessTime: a.lastUpdate,
          reconnectRequired: true,
        },
      },
    }),
  };
}

// Connected-account names are generated on the (separately loaded) bank-connect
// screen via i18n. Translations load asynchronously (HttpBackend, no suspense),
// so a cold start can bake the raw key into the persisted connection before the
// bundle arrives. Resolve such keys here, at response time, when the app is
// fully booted and the bundle is guaranteed loaded — this also heals
// connections persisted by an earlier build. Falls back to English because
// cs.json carries these demo-only keys via Lokalise, not this repo.
const ACCOUNT_NAME_KEY_RE =
  /^(?:bw-react-app:)?demoBankConnect\.accountNames\.(\w+)$/;

function resolveAccountName(name: string): string {
  const suffix = ACCOUNT_NAME_KEY_RE.exec(name)?.[1];
  // Already a human-readable name → leave it; otherwise heal a baked-in key
  // (from a connection persisted before the bilingual map landed) via the same
  // static map, so it resolves in any language with no i18n bundle dependency.
  return suffix ? accountNameLabel(suffix, i18n.language) : name;
}

// Account doc for an account added at runtime via the demo connect-bank flow.
// Carries its own bank presentation (the connect flow uses real paymentProvider
// banks, not our static DEMO_BANKS seed).
function addedAccountDoc(
  a: DemoConnectedAccount,
): DeepPartial<LiveBankAccountDoc> {
  const company =
    DEMO_COMPANY_BY_ID[a.companyId as keyof typeof DEMO_COMPANY_BY_ID];
  return {
    documentHeader: {
      id: a.id,
      documentType: "LiveBankAccount",
      _ts: Math.floor(Date.now() / 1000),
      _etag: '"demo-etag"',
    },
    bankAccount: {
      entityData: {
        name: resolveAccountName(a.name),
        displayBankFormat: "ibanFormat",
        ibanFormat: { iban: a.iban, bic: a.bic },
        bbanFormat: { accountNo: a.accountNumber, bankCode: a.bic.slice(0, 4) },
        additionalBankInfo: { bankName: a.bankName },
        bank: bankForBic(a.bic),
        currency: a.currency,
        ownersNames: company ? [company.name] : [],
      },
    },
    bankAccountBalance: {
      entityData: {
        // Top-level fields read by the cockpit customize-accounts transform.
        currentBalance: a.balance,
        currency: a.currency,
        originalAmount: {
          amount: a.balance,
          currency: a.currency,
          timestamp:
            archivedAt(a.id) ?? new Date(Date.now() - 3600_000).toISOString(),
        },
        amounts: {
          amountEUR: {
            amount: Math.round(a.balance * rateToEur(a.currency)),
            currency: "EUR",
          },
        },
        // Must be from today, otherwise the dashboard flags the balance as
        // derived/inaccurate (isDerivedBalance checks the calendar day).
        balanceDateTime: new Date(Date.now() - 3600_000).toISOString(),
      },
    },
    partyRoleRef: {
      entityData: {
        documentType: "LiveCompany",
        documentId: a.companyId,
        entityId: a.companyId,
      },
    },
    partyRoleAdditionalInfo: { entityData: { roleName: company?.name ?? "" } },
    clientCustomInfo: { entityData: { bankAccountName: a.name } },
    connectionInformation: {
      entityData: {
        status: "CONNECTED",
        daysToExpire: 90,
        validTo: new Date(Date.now() + 90 * 86_400_000).toISOString(),
        paymentProvider: paymentProviderForBic(a.bic),
      },
    },
    docEntityStateFlag: { entityData: { isArchived: isArchivedAccountId(a.id) } },
  };
}

// One connection whose bank access has expired (RC9, CAS-1458): shows the
// expiry icon + "Go to accounts" banner, and can be fixed via reconnect.
const DEMO_EXPIRED_ACCOUNT_IDS = new Set(["demo-acc-ventures-5"]);

// One account whose history download was cut short by an expired SCA token
// (RC9, CAS-1381/1616/1675): "Incomplete history" banner + reconnect prompt.
const DEMO_INCOMPLETE_HISTORY_ACCOUNT_IDS = new Set(["demo-acc-labs-2"]);

function hasIncompleteDemoHistory(accountId: string): boolean {
  return (
    DEMO_INCOMPLETE_HISTORY_ACCOUNT_IDS.has(accountId) &&
    !isConnectionRefreshed(accountId)
  );
}

function isExpiredDemoConnection(accountId: string): boolean {
  return (
    DEMO_EXPIRED_ACCOUNT_IDS.has(accountId) && !isConnectionRefreshed(accountId)
  );
}

/** Accounts that count in overviews, charts and totals (not archived). */
function activeDemoAccounts(): DemoAccount[] {
  return DEMO_ACCOUNTS.filter((a) => !isArchivedAccountId(a.id));
}

function buildAccountList(req: MockRequest) {
  const filtering = (req.body?.filtering ?? []) as Array<{
    filteringAttribute: string;
    attributeValue: unknown;
  }>;
  const companyArrayFilter = filtering.find(
    (f) => f.filteringAttribute === "companyIdArray",
  )?.attributeValue;
  const companyFilter = filtering.find(
    (f) => f.filteringAttribute === "companyId",
  )?.attributeValue;
  // Single-account fetches (e.g. useGetAccountUsers on the account detail)
  // filter by documentId — without honouring it the FE takes documentList[0],
  // which is a different company's account.
  const documentIdFilter = filtering.find(
    (f) => f.filteringAttribute === "documentId",
  )?.attributeValue;

  let pool = DEMO_ACCOUNTS;
  let added = getAddedAccounts();
  if (typeof documentIdFilter === "string") {
    pool = pool.filter((a) => a.id === documentIdFilter);
    added = added.filter((a) => a.id === documentIdFilter);
  }
  if (Array.isArray(companyArrayFilter) && companyArrayFilter.length) {
    const set = new Set(companyArrayFilter.map(String));
    pool = pool.filter((a) => set.has(a.companyId));
    added = added.filter((a) => set.has(a.companyId));
  } else if (typeof companyFilter === "string") {
    pool = pool.filter((a) => a.companyId === companyFilter);
    added = added.filter((a) => a.companyId === companyFilter);
  }
  // Company scope by party (reconnect banners on company / analytics pages).
  const partyFilter = filtering.find(
    (f) => f.filteringAttribute === "partyId",
  )?.attributeValue;
  if (typeof partyFilter === "string") {
    pool = pool.filter((a) => a.companyId === partyFilter);
    added = added.filter((a) => a.companyId === partyFilter);
  }
  const archivedFilter = filtering.find(
    (f) => f.filteringAttribute === "isArchived",
  )?.attributeValue;
  if (typeof archivedFilter === "boolean") {
    pool = pool.filter((a) => isArchivedAccountId(a.id) === archivedFilter);
    added = added.filter((a) => isArchivedAccountId(a.id) === archivedFilter);
  }
  const docs = [...pool.map(accountDoc), ...added.map(addedAccountDoc)];
  // Without explicit paging the backend returns every account; a default
  // 20-item page would silently drop accounts (e.g. from the expiry banner).
  return paged(
    docs,
    req.body?.paging ? req.body : { ...req.body, paging: { pageSize: docs.length || 1 } },
  );
}

// paymentProvider enum value for a BIC — seeded accounts only know their bank,
// but the reconnect / restore-from-archive flow starts from the provider.
function paymentProviderForBic(bic: string | undefined): PaymentProvider | undefined {
  if (!bic) return undefined;
  const prefix = bic.slice(0, 8).toUpperCase();
  const matches = paymentProviderEnum.values.filter(
    (p) => typeof p.bank === "string" && p.bank.slice(0, 8).toUpperCase() === prefix,
  );
  // Prefer the real provider over its sandbox (isMock) twin.
  return (matches.find((p) => !("isMock" in p && p.isMock)) ?? matches[0])?.value;
}

// Bank enum value (BIC) for the account's bank — the archive / remove dialogs
// resolve the bank name from `bankAccount.bank`.
function bankForBic(bic: string | undefined): Bank | undefined {
  if (!bic) return undefined;
  const prefix = bic.slice(0, 8).toUpperCase();
  return bankEnum.values.find((b) =>
    b.bics.some((x) => x.slice(0, 8).toUpperCase() === prefix),
  )?.value;
}

const normalizeIban = (iban: string | undefined) =>
  (iban ?? "").replace(/\s+/g, "").toUpperCase();

/** Existing (seeded or connected) account with this IBAN + currency. */
function existingAccountByIban(
  iban: string,
  currency: string,
): { id: string } | undefined {
  const key = normalizeIban(iban);
  return (
    DEMO_ACCOUNTS.find(
      (a) => normalizeIban(a.iban) === key && a.currency === currency,
    ) ??
    getAddedAccounts().find(
      (a) => normalizeIban(a.iban) === key && a.currency === currency,
    )
  );
}

// createDocument("ReqArchiveBankAccounts") — archive the referenced accounts.
function archiveBankAccountsRequest(req: MockRequest) {
  const body = (req.body ?? {}) as {
    documentType?: string;
    entityData?: { bankAccountRefs?: Array<{ documentId?: string }> };
  };
  if (body.documentType !== "ReqArchiveBankAccounts") return null;
  const ids = (body.entityData?.bankAccountRefs ?? [])
    .map((r) => r.documentId)
    .filter((id): id is string => typeof id === "string");
  archiveAccounts(ids);
  const ts = Math.floor(Date.now() / 1000);
  return {
    documentType: "ReqArchiveBankAccounts",
    documentHeader: {
      id: `demo-req-archive-${ts}`,
      documentType: "ReqArchiveBankAccounts",
      definitionVersion: "1.0.0",
      _ts: ts,
      _etag: '"demo-etag"',
    },
  };
}

// reconnect — refresh an expired connection or restore an archived account.
// Routes through the demo bank screen, which offers the same account again so
// the assignment dialog pre-selects it.
function buildReconnect(req: MockRequest) {
  const body = (req.body ?? {}) as { documentId?: string; callbackUrl?: string };
  const documentId = String(body.documentId ?? "");
  const callbackUrl = String(body.callbackUrl ?? window.location.origin);
  const origin = new URL(callbackUrl, window.location.origin).origin;
  const connectionId = `demo-conn-${Date.now()}`;
  rememberReconnect(connectionId, documentId);
  const seed = DEMO_ACCOUNT_BY_ID[documentId];
  const added = getAddedAccounts().find((a) => a.id === documentId);
  const provider =
    paymentProviderForBic(seed ? DEMO_BANKS[seed.bankKey]?.bic : added?.bic) ?? "";
  const redirectUrl = `${origin}/demo-bank-connect?provider=${encodeURIComponent(
    provider,
  )}&connectionId=${connectionId}&reconnect=${encodeURIComponent(
    documentId,
  )}&callback=${encodeURIComponent(callbackUrl)}`;
  return { redirectUrl };
}

function buildClientCompanyList() {
  return DEMO_COMPANIES.map((c) => ({
    partyRoleRef: { documentId: c.id, partyRoleId: `${c.id}-master` },
    partyRoleAdditionalInfo: {
      roleName: c.name,
      companyRegistrationNumber: c.registrationNumber,
      imgSrc: c.imgSrc,
    },
  }));
}

// ────────────────────────────────────────────────────────────────────────────
// Connect-bank flow (demo)
//
// The real flow redirects to the bank's PSD2 page. In the demo we redirect to
// an in-app fake bank screen (/demo-bank-connect), which persists the picked
// accounts; the existing assignment + authorize steps then complete against the
// handlers below, and the accounts land in the account list.
// ────────────────────────────────────────────────────────────────────────────

// initAddBankAccounts → redirect URL pointing at the in-app demo bank screen.
function buildInitAddBankAccounts(req: MockRequest) {
  const body = (req.body ?? {}) as Record<string, unknown>;
  const paymentProvider = String(body.paymentProvider ?? "");
  const callbackUrl = String(body.callbackUrl ?? window.location.origin);
  const origin = new URL(callbackUrl, window.location.origin).origin;
  const connectionId = `demo-conn-${Date.now()}`;
  const redirectUrl = `${origin}/demo-bank-connect?provider=${encodeURIComponent(
    paymentProvider,
  )}&connectionId=${connectionId}&callback=${encodeURIComponent(callbackUrl)}`;
  return { redirectUrl };
}

// createDocument("ReqAssignBankAccounts", ...) → the accounts the user shared on
// the fake bank screen, shaped for the assignment dialog.
function buildConnectClientAccounts(req: MockRequest) {
  const body = (req.body ?? {}) as {
    documentType?: string;
    entityData?: { connectionId?: string; paymentProvider?: string };
    connectionId?: string;
  };
  if (body.documentType && body.documentType !== "ReqAssignBankAccounts") {
    return { documentId: "demo-doc-new" };
  }
  const connectionId = body.entityData?.connectionId ?? body.connectionId ?? "";
  // RC10: pre-fill the company the user started connecting from.
  const originCompanyId = (body.entityData as { originCompanyId?: string } | undefined)
    ?.originCompanyId;
  if (originCompanyId) {
    for (const acc of getPendingConnection(connectionId)?.accounts ?? []) {
      if (!acc.companyFromUser && acc.companyId !== originCompanyId) {
        updatePendingAccount(acc.id, { companyId: originCompanyId });
      }
    }
  }
  const pending = getPendingConnection(connectionId);
  // Accounts already in Fidoo and not archived are just refreshed by the new
  // consent (no assignment needed); archived ones are offered for restore.
  const accounts = (pending?.accounts ?? []).filter((acc) => {
    const existing = existingAccountByIban(acc.iban, acc.currency);
    if (existing && !isArchivedAccountId(existing.id)) {
      markConnectionsRefreshed([existing.id]);
      return false;
    }
    return true;
  });

  return {
    documentType: "ReqAssignBankAccounts",
    documentHeader: {
      id: `reqassign-${connectionId}`,
      documentType: "ReqAssignBankAccounts",
      definitionVersion: "1.0.0",
      _ts: Math.floor(Date.now() / 1000),
      _etag: '"demo-etag"',
    },
    accounts: {
      pagingResponse: {
        totalItemsCount: accounts.length,
        hasNextPage: false,
        pageNumber: 1,
        pageSize: 50,
      },
      returnedItems: accounts.map((acc) => {
        const company =
          DEMO_COMPANY_BY_ID[acc.companyId as keyof typeof DEMO_COMPANY_BY_ID];
        return {
          entityHeader: {
            entityId: acc.id,
            entityType: "Account",
            definitionVersion: "1.0.0",
            entityContext: "ReqAssignBankAccounts",
          },
          entityData: {
            bankAccount: {
              entityData: {
                name: resolveAccountName(acc.name),
                displayBankFormat: "ibanFormat",
                ibanFormat: { iban: acc.iban, bic: acc.bic },
                bbanFormat: { accountNo: acc.accountNumber },
                additionalBankInfo: { bankName: acc.bankName },
                currency: acc.currency,
                ownersNames: company ? [company.name] : [],
              },
            },
            bankAccountBalance: {
              entityData: {
                currentBalance: acc.balance,
                currency: acc.currency,
                originalAmount: { amount: acc.balance, currency: acc.currency },
              },
            },
            companyRef: {
              entityData: {
                documentId: acc.companyId,
                documentType: "LiveCompany",
              },
            },
            companyAdditionalInfo: {
              entityData: { roleName: company?.name ?? "" },
            },
            ...(acc.selected !== undefined && {
              assignmentDetail: {
                entityData: {
                  status: acc.selected ? "Assigned" : "NotAssigned",
                },
              },
            }),
            // The backend recognises an account that is already in Fidoo
            // but archived; the dialog pre-selects it to restore it.
            docEntityStateFlag: {
              entityData: {
                isArchived: isArchivedAccountId(
                  existingAccountByIban(acc.iban, acc.currency)?.id ?? "",
                ),
              },
            },
          },
        };
      }),
    },
    processingInfo: {
      entityData: {
        activities: {
          pagingResponse: {
            totalItemsCount: 1,
            hasNextPage: false,
            pageNumber: 1,
            pageSize: 1,
          },
          returnedItems: [
            { entityHeader: { entityId: `activity-${connectionId}` } },
          ],
        },
      },
    },
  };
}

// createEntity (authorize accounts) → persist the pending connection's accounts
// into the demo account list, then report success.
function authorizeConnectAccounts(req: MockRequest) {
  // Accounts that already exist (restored from the archive or a refreshed
  // connection) are reactivated instead of being added a second time.
  commitAllPendingConnections((account) => {
    const existing = existingAccountByIban(account.iban, account.currency);
    if (!existing) return false;
    // Pre-selected by the dialog unless the user unticked it.
    if (account.selected !== false) {
      unarchiveAccounts([existing.id]);
      markConnectionsRefreshed([existing.id]);
    }
    return true;
  });
  const body = (req.body ?? {}) as { documentId?: string };
  return { documentId: body.documentId ?? "demo-doc-new" };
}

// ────────────────────────────────────────────────────────────────────────────
// Transactions
// ────────────────────────────────────────────────────────────────────────────

function transactionDoc(
  t: (typeof DEMO_TRANSACTIONS)[number],
): DeepPartial<LiveTransactionDoc> {
  const account = DEMO_ACCOUNT_BY_ID[t.accountId];
  const bank = account ? DEMO_BANKS[account.bankKey] : undefined;
  const company = DEMO_COMPANY_BY_ID[t.companyId];
  return {
    documentHeader: {
      id: t.id,
      documentType: "LiveTransaction",
      _ts: Math.floor(new Date(t.bookedAt).getTime() / 1000),
      _etag: '"demo-etag"',
    },
    transactionBasicInfo: {
      entityData: {
        displayDate: t.bookedAt,
        bookingDate: t.bookedAt,
        valueDate: t.valueAt,
        method: t.method,
        direction: t.direction,
        category: t.category,
        variableSymbol: t.variableSymbol,
        constantSymbol: t.constantSymbol,
        specificSymbol: t.specificSymbol,
        remittanceInformation: t.description,
      },
    },
    transactionAmount: {
      entityData: { amount: t.amount, currency: t.currency },
    },
    // Per-currency converted amounts. The cockpit category drill-down reads the
    // amount via `amounts.entityData.amounts.amount{CURRENCY}.amount` (e.g.
    // amountEUR) so it can show every transaction in the analytics view's
    // currency. Populate all supported currencies, converted through the same
    // rateToEur used by the cash-flow aggregation so the popover totals match
    // the cashflow-table cell exactly.
    amounts: {
      entityData: {
        amounts: transactionAmountsByCurrency(t.amount, t.currency),
      },
    },
    counterBankAccountAdditionalInfo: {
      entityData: {
        name: t.counterpartyName,
        accountNo: t.counterpartyAccount,
      },
    },
    counterBankAccountRef: {
      entityData: { documentType: "LiveBankAccount" },
    },
    bankAccountAdditionalInfo: {
      entityData: {
        name: account?.name ?? "—",
        additionalBankInfo: { bankName: bank?.name },
      },
    },
    bankAccountRef: {
      entityData: { documentId: t.accountId, documentType: "LiveBankAccount" },
    },
    partyRoleRef: {
      entityData: {
        documentType: "ClientCompany",
        documentId: `${t.companyId}-master`,
        entityId: company.id,
      },
    },
    partyRoleAdditionalInfo: {
      entityData: { roleName: company.name },
    },
    counterBankAccountOwner: {
      entityData: { name: t.counterpartyName },
    },
    enrichedData: {
      entityData: {
        category: t.category,
        merchantName: t.counterpartyName,
      },
    },
  };
}

function buildTransactionList(req: MockRequest) {
  const filtering = (req.body?.filtering ?? []) as Array<{
    filteringAttribute: string;
    attributeValue: unknown;
  }>;
  const find = (attr: string) =>
    filtering.find((f) => f.filteringAttribute === attr)?.attributeValue;

  let pool = DEMO_TRANSACTIONS;

  // Overview tables exclude archived accounts; the account detail keeps them.
  if (find("isArchivedBankAccount") === false) {
    pool = pool.filter((t) => !isArchivedAccountId(t.accountId));
  }

  // Single-account filter (account-detail transactions table).
  const accountId = find("bankAccountId") ?? find("accountId");
  if (typeof accountId === "string") {
    pool = pool.filter((t) => t.accountId === accountId);
  }

  // Multi-account filter — used by the cockpit category drill-down
  // (useCategoryTransactions sends `bankAccountIdArray`).
  const accountIdArray = find("bankAccountIdArray");
  if (Array.isArray(accountIdArray) && accountIdArray.length > 0) {
    const set = new Set(accountIdArray as string[]);
    pool = pool.filter((t) => set.has(t.accountId));
  }

  const companyFilter = find("companyId");
  if (typeof companyFilter === "string") {
    pool = pool.filter((t) => t.companyId === companyFilter);
  }

  // Date-range filter on the displayed (booking) date. The drill-down sends
  // `displayDateFrom` / `displayDateTo` matching the clicked period's
  // dateFrom/dateTo (inclusive) — bucket those same transactions here so the
  // popover list equals the cashflow-table cell.
  const displayDateFrom = find("displayDateFrom");
  if (typeof displayDateFrom === "string" && displayDateFrom) {
    pool = pool.filter((t) => t.bookedAt.slice(0, 10) >= displayDateFrom);
  }
  const displayDateTo = find("displayDateTo");
  if (typeof displayDateTo === "string" && displayDateTo) {
    pool = pool.filter((t) => t.bookedAt.slice(0, 10) <= displayDateTo);
  }

  // Direction (IN / OUT) — the drill-down distinguishes income vs cost cells.
  const direction = find("direction");
  if (direction === "IN" || direction === "OUT") {
    pool = pool.filter((t) => t.direction === direction);
  }

  // Category filter — the drill-down sends `categoryArray: [categoryId]`.
  const categoryArray = find("categoryArray");
  if (Array.isArray(categoryArray) && categoryArray.length > 0) {
    const set = new Set(categoryArray as string[]);
    pool = pool.filter((t) => set.has(t.category));
  }

  return paged(pool.map(transactionDoc), req.body);
}

// readEntities returns a SINGLE document (not a list). The cockpit category
// drill-down opens a transaction detail via readEntities with a
// `documentReference.documentId`; return that one transaction's doc so the
// detail shows real amounts/counterparty instead of an empty/zero record.
function buildTransactionEntity(req: MockRequest) {
  const body = (req.body ?? {}) as {
    documentReference?: { documentId?: string };
    documentId?: string;
  };
  const id = body.documentReference?.documentId ?? body.documentId;
  const tx =
    (typeof id === "string" && DEMO_TRANSACTIONS.find((t) => t.id === id)) ||
    DEMO_TRANSACTIONS[0];
  return transactionDoc(tx);
}

function buildTransactionSums(req: MockRequest) {
  const currency =
    (req.body?.graphCurrency as DemoAccountCurrency | undefined) ?? "EUR";
  const dayBuckets = new Map<string, { moneyIn: number; moneyOut: number }>();
  for (const t of DEMO_TRANSACTIONS) {
    if (isArchivedAccountId(t.accountId)) continue;
    const day = t.bookedAt.slice(0, 10);
    const converted =
      t.currency === currency
        ? t.amount
        : t.amount * (rateToEur(t.currency) / rateToEur(currency));
    const bucket = dayBuckets.get(day) ?? { moneyIn: 0, moneyOut: 0 };
    if (converted > 0) bucket.moneyIn += converted;
    else bucket.moneyOut += -converted;
    dayBuckets.set(day, bucket);
  }
  const summaryArray = [...dayBuckets.entries()]
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([timestamp, b]) => ({
      timestamp,
      moneyIn: Math.round(b.moneyIn),
      moneyOut: Math.round(b.moneyOut),
      netChange: Math.round(b.moneyIn - b.moneyOut),
    }));
  return { summaryArray, currency };
}

// ────────────────────────────────────────────────────────────────────────────
// Client settings (per-company dashboard / analytics prefs)
// ────────────────────────────────────────────────────────────────────────────

// Earliest seeded transaction date. The cockpit clamps period navigation to
// maxDateRangeFrom (and disables the "previous" arrow there). Pinning it to the
// real data start stops users from paging back into empty months — the bug
// where Month view + "previous" showed "no analytics data".
const DEMO_DATA_START = DEMO_TRANSACTIONS.reduce(
  (min, t) => (t.bookedAt < min ? t.bookedAt : min),
  DEMO_TRANSACTIONS[0]?.bookedAt ?? new Date().toISOString(),
).slice(0, 10);

const DEFAULT_HEALTH_THRESHOLDS = { thresholdBad: 0.4, thresholdGood: 0.7 };

// createDocument on LiveClientSettings: currency, categorization switch and
// cash-flow settings (analytics account selection + health thresholds).
function clientSettingsCreateDocument(req: MockRequest) {
  const body = (req.body ?? {}) as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  const documentType = String(body.documentType ?? "");
  const companyId = companyFromSettingsDocId(body.masterDocumentReference?.documentId);
  const data = body.entityData ?? {};
  if (companyId) {
    if (documentType === "ReqUpdateCurrencySettings") {
      updateCompanySettings(companyId, { currency: data.currencySettings?.currency });
    } else if (documentType === "ReqUpdateTransactionCategorizationSettings") {
      updateCompanySettings(companyId, {
        categorizationEnabled: data.transactionCategorizationSettings?.isEnabled === true,
      });
    } else if (documentType === "ReqUpdateCashFlowSettings") {
      const cfs = data.cashFlowSettings ?? {};
      updateCompanySettings(companyId, {
        ...(Array.isArray(cfs.selectedBankAccountRefArray) && {
          selectedAccountIds: cfs.selectedBankAccountRefArray
            .map((r: { documentId?: string }) => r.documentId)
            .filter(Boolean),
        }),
        ...(cfs.healthIndicator && { healthThresholds: cfs.healthIndicator }),
      });
    }
  }
  const ts = Math.floor(Date.now() / 1000);
  return {
    documentType,
    documentHeader: {
      id: `demo-${documentType}-${ts}`,
      documentType,
      definitionVersion: "1.0.0",
      _ts: ts,
      _etag: '"demo-etag"',
    },
    ...(companyId && documentType === "ReqUpdateCashFlowSettings"
      ? { cashFlowSettings: { entityData: data.cashFlowSettings ?? {} } }
      : {}),
  };
}

function buildClientSettingsList(req: MockRequest) {
  const filtering = (req.body?.filtering ?? []) as Array<{
    filteringAttribute: string;
    attributeValue: unknown;
  }>;
  const partyIdFilter = filtering.find(
    (f) =>
      f.filteringAttribute === "partyId" ||
      f.filteringAttribute === "companyId",
  )?.attributeValue;

  const companyIds =
    typeof partyIdFilter === "string"
      ? [partyIdFilter]
      : DEMO_COMPANIES.map((c) => c.id);

  const docs = companyIds.map((companyId) => {
    const company =
      DEMO_COMPANY_BY_ID[companyId as keyof typeof DEMO_COMPANY_BY_ID];
    const stored = getCompanySettings(companyId);
    const activeAccounts = activeDemoAccounts().filter(
      (a) => a.companyId === companyId,
    );
    const accounts = stored.selectedAccountIds
      ? activeAccounts.filter((a) => stored.selectedAccountIds!.includes(a.id))
      : activeAccounts;
    return {
      documentHeader: {
        id: `demo-clientsettings-${companyId}`,
        documentType: "LiveClientSettings",
      },
      cashFlowSettings: {
        entityData: {
          selectedBankAccountRefArray: accounts.map((a) => ({
            documentId: a.id,
            documentType: "LiveBankAccount",
          })),
          healthIndicator: stored.healthThresholds ?? DEFAULT_HEALTH_THRESHOLDS,
        },
      },
      currencySettings: {
        entityData: {
          currency: stored.currency ?? company?.defaultCurrency ?? "EUR",
        },
      },
      transactionCategorizationSettings: {
        entityData: stored.categorizationEnabled
          ? { isEnabled: true, historyDepth: "MONTHS_3" }
          : { isEnabled: false },
      },
      virtualCalendarPropertiesData: {
        entityData: {
          maxDateRangeFrom: DEMO_DATA_START,
          // Anchor the upper bound to the real "now" (+ margin) so the cockpit's
          // default weekly/monthly window — which ends at today — is NOT clamped
          // into the past. DEMO_TRANSACTIONS are seeded relative to new Date(),
          // so a hardcoded past date here would hide the most recent activity
          // and leave the default view looking empty.
          maxDateRangeTo: new Date(Date.now() + 150 * 24 * 3600_000)
            .toISOString()
            .slice(0, 10),
        },
      },
      partyRoleAdditionalInfo: {
        entityData: {
          selectedBankAccountRefArray: accounts.map((a) => ({
            documentId: a.id,
            documentType: "LiveBankAccount",
          })),
        },
      },
    };
  });

  return paged(docs, req.body);
}

// ────────────────────────────────────────────────────────────────────────────
// Counterparties
// ────────────────────────────────────────────────────────────────────────────

function buildCounterpartyList(req: MockRequest) {
  const docs = DEMO_CONTACTS.map((c) => ({
    documentHeader: {
      id: c.id,
      documentType: "Counterparty",
      _ts: Math.floor(new Date(c.lastTransactionAt).getTime() / 1000),
      _etag: '"demo-etag"',
    },
    entityData: {
      counterpartyId: c.id,
      counterpartyName: c.name,
      partyName: c.name,
      registrationNumber: c.registrationNumber,
      vatNumber: c.vatNumber,
      iban: c.iban,
      bankAccountNumber: c.iban,
      bankCode: c.bankBic.slice(0, 4),
      bankName: c.bankName,
      bic: c.bankBic,
      email: c.email,
      phone: c.phone,
      address: {
        street: c.street,
        city: c.city,
        zip: c.zip,
        country: c.country,
      },
      category: c.category,
      tags: c.tags,
      totals: {
        spent: c.totalSpent,
        received: c.totalReceived,
        currency: c.currency,
      },
      lastTransactionAt: c.lastTransactionAt,
      imgSrc: c.imgSrc,
    },
  }));
  return paged(docs, req.body);
}

// ────────────────────────────────────────────────────────────────────────────
// Party (people / team)
// ────────────────────────────────────────────────────────────────────────────

// Real company-access roles live in the PartyRoleRelationType enum. The values
// actually used for company team members (see @dfp/bw-react-company-user-settings
// supportedRoles) are: WorkspaceOwner, Admin, FinancialSpecialist, Member.
//
// We derive the role from the person's JOB TITLE rather than the seed's
// arbitrary per-company access flag, so the displayed role is coherent with who
// the person is: owners/CEOs run the workspace, finance staff are financial
// specialists, everyone else is a plain member. (Previously a CFO showed as
// "Administrator" — a finance person should be a Financial Specialist.)
// "Member" went live with bw-2609-1-1, so plain employees are Members.
const DEMO_JOB_TO_RELATION_TYPE: Record<
  DemoTeamMember["jobTitle"],
  string | undefined
> = {
  FounderOwner: "WorkspaceOwner",
  CEO: "WorkspaceOwner",
  CFO: "FinancialSpecialist",
  FinancialManager: "FinancialSpecialist",
  Accountant: "FinancialSpecialist",
  Other: "Member",
};

// People without a live production role (no chip) are hidden from all
// team-ish surfaces — production has no role-less workspace members.
function hasLiveRole(m: DemoTeamMember): boolean {
  return DEMO_JOB_TO_RELATION_TYPE[m.jobTitle] !== undefined;
}

function personDoc(m: DemoTeamMember) {
  // The company team list converter (companyTeamList.utils.ts) iterates
  // `roles.returnedItems[].entityData.roleBasicInfo.entityData.masterRoleRef`
  // looking for entries that match the active companyDocumentId. Each
  // membership has to be its own role entry.
  return {
    documentHeader: {
      id: m.id,
      documentType: "LivePerson",
      _ts: Math.floor(Date.now() / 1000),
      _etag: '"demo-etag"',
    },
    personBasicInfo: {
      entityData: {
        firstName: m.firstName,
        surname: m.surname,
        avatarUrl: m.avatarUrl,
      },
    },
    partyRoleRef: {
      entityData: { partyRoleId: m.partyRoleId, partyRoleType: "ClientUser" },
    },
    partyRoleAdditionalInfo: {
      entityData: {
        roleName: `${m.firstName} ${m.surname}`,
        jobTitle: m.jobTitle,
        jobTitleCustom: m.jobTitleCustom,
        imgSrc: m.avatarUrl,
      },
    },
    identifiers: {
      returnedItems: [
        {
          documentHeader: {
            id: `${m.id}-username`,
            documentType: "PartyIdentifier",
          },
          // getContactInformation (team member detail) matches identifiers by
          // entityHeader.entityId — without it the page throws.
          entityHeader: { entityId: `${m.id}-username` },
          entityData: {
            partyIdentifierType: "UserName",
            partyIdentifierValue: m.email,
          },
        },
        ...(m.phone
          ? [
              {
                documentHeader: {
                  id: `${m.id}-phone`,
                  documentType: "PartyIdentifier",
                },
                entityHeader: { entityId: `${m.id}-phone` },
                entityData: {
                  partyIdentifierType: "MfaPhone",
                  partyIdentifierValue: m.phone,
                },
              },
            ]
          : []),
      ],
    },
    roles: {
      returnedItems: m.memberships.map((mem) => ({
        documentHeader: {
          id: `${m.partyRoleId}-${mem.companyId}`,
          documentType: "PartyRole",
        },
        // useRowSettings (company team table) reads role.entityHeader.entityId
        // for the row id / current-user match — must be present or the row
        // render throws.
        entityHeader: { entityId: `${m.partyRoleId}-${mem.companyId}` },
        entityData: {
          // The company team table reads name/email/role from inside each
          // ROLE's entityData (getUserData + ColumnRole), not from the doc
          // top level — so person info must be nested per role.
          personBasicInfo: {
            entityData: {
              firstName: m.firstName,
              surname: m.surname,
              avatarUrl: m.avatarUrl,
            },
          },
          contacts: {
            returnedItems: [
              {
                documentHeader: {
                  id: `${m.id}-${mem.companyId}-email`,
                  documentType: "Contact",
                },
                entityData: {
                  contactType: "Email",
                  emailDetail: { email: m.email },
                },
              },
            ],
          },
          roleBasicInfo: {
            entityData: {
              roleType: "ClientUser",
              roleName: `${m.firstName} ${m.surname}`,
              jobTitle: m.jobTitle,
              jobTitleCustom: m.jobTitleCustom,
              masterRoleRef: {
                documentId: mem.companyId,
                documentType: "LiveCompany",
              },
            },
          },
          // ColumnRole reads roleRelations[0].roleRelationBasicInfo.roleRelationType.
          // Map the seed's role onto the real PartyRoleRelationType enum values.
          roleRelations: {
            returnedItems: [
              {
                documentHeader: {
                  id: `${m.partyRoleId}-${mem.companyId}-rel`,
                  documentType: "PartyRoleRelation",
                },
                entityData: {
                  roleRelationBasicInfo: {
                    entityData: {
                      roleRelationType: DEMO_JOB_TO_RELATION_TYPE[m.jobTitle],
                    },
                  },
                  // getUsernameForCompany resolves the e-mail by following
                  // identifierRefs to the UserName identifier above.
                  identifierRefs: {
                    returnedItems: [
                      { entityData: { entityId: `${m.id}-username` } },
                    ],
                  },
                },
              },
            ],
          },
          partyRoleReference: {
            partyId: m.id,
            partyRoleId: m.partyRoleId,
            partyRoleType: "ClientUser",
          },
          person2clientInfo: {
            entityData: {
              status: m.status,
              accessLevel: mem.role,
              invitedAt: m.invitedAt,
              lastActiveAt: m.lastActiveAt,
            },
          },
        },
      })),
    },
    statusInfo: {
      entityData: {
        status: m.status,
        invitedAt: m.invitedAt,
        lastActiveAt: m.lastActiveAt,
      },
    },
  };
}

// Anna's own LivePerson doc. The "Mé role" detail page calls
// useUserOrInvitationQuery(DEMO_LIVE_PERSON_ID) and matches the role by
// role.entityHeader.entityId === roleId, where roleId on the row is
// DEMO_CLIENT_ROLE_BY_COMPANY[companyId]. One role entry per company.
function selfPersonDoc() {
  const annaRoleToRelation: Record<string, string | undefined> = {
    CFO: "FinancialSpecialist",
    Admin: "WorkspaceOwner",
    Employee: "Member",
  };
  return {
    documentHeader: {
      id: DEMO_LIVE_PERSON_ID,
      documentType: "LivePerson",
      _ts: Math.floor(Date.now() / 1000),
      _etag: '"demo-etag"',
    },
    personBasicInfo: {
      entityData: {
        firstName: DEMO_USER.firstName,
        surname: DEMO_USER.lastName,
      },
    },
    partyRoleRef: {
      entityData: {
        partyRoleId: DEMO_REGISTERED_ROLE_ID,
        partyRoleType: "RegisteredUser",
      },
    },
    roles: {
      returnedItems: DEMO_COMPANIES.map((c) => ({
        documentHeader: {
          id: DEMO_CLIENT_ROLE_BY_COMPANY[c.id],
          documentType: "PartyRole",
        },
        entityHeader: { entityId: DEMO_CLIENT_ROLE_BY_COMPANY[c.id] },
        entityData: {
          personBasicInfo: {
            entityData: {
              firstName: DEMO_USER.firstName,
              surname: DEMO_USER.lastName,
            },
          },
          roleBasicInfo: {
            entityData: {
              roleType: "ClientUser",
              roleName: `${DEMO_USER.firstName} ${DEMO_USER.lastName}`,
              jobTitle: c.annaRole,
              masterRoleRef: {
                documentId: c.id,
                documentType: "LiveCompany",
              },
            },
          },
          roleRelations: {
            returnedItems: [
              {
                documentHeader: {
                  id: `${DEMO_CLIENT_ROLE_BY_COMPANY[c.id]}-rel`,
                  documentType: "PartyRoleRelation",
                },
                entityData: {
                  roleRelationBasicInfo: {
                    entityData: {
                      roleRelationType: annaRoleToRelation[c.annaRole],
                    },
                  },
                },
              },
            ],
          },
          partyRoleReference: {
            partyId: DEMO_LIVE_PERSON_ID,
            partyRoleId: DEMO_CLIENT_ROLE_BY_COMPANY[c.id],
            partyRoleType: "ClientUser",
          },
        },
      })),
    },
  };
}

// Company person filters (getCompanyFilterAttribute) nest the target company's
// documentId under roleTypeArray → filteringByArrayValue[].filtering[] →
// roleTypeMasterRoleEntityRef. Walk the tree to pull it out.
function extractCompanyIdFromFilter(filtering: unknown): string | undefined {
  if (!Array.isArray(filtering)) {
    return undefined;
  }
  for (const entry of filtering) {
    const f = entry as {
      filteringAttribute?: string;
      attributeValue?: { documentId?: string };
      filtering?: unknown;
      filteringByArrayValue?: Array<{ filtering?: unknown }>;
    };
    if (
      f.filteringAttribute === "roleTypeMasterRoleEntityRef" &&
      f.attributeValue?.documentId
    ) {
      return f.attributeValue.documentId;
    }
    if (Array.isArray(f.filteringByArrayValue)) {
      for (const arrayValue of f.filteringByArrayValue) {
        const found = extractCompanyIdFromFilter(arrayValue?.filtering);
        if (found) return found;
      }
    }
    const nested = extractCompanyIdFromFilter(f.filtering);
    if (nested) return nested;
  }
  return undefined;
}

function partyGetDocumentList(req: MockRequest) {
  const readDocuments = (req.body?.readDocuments ?? {}) as Record<
    string,
    unknown
  >;
  if ("LivePerson" in readDocuments || "Person" in readDocuments) {
    const filtering = (req.body?.filtering ?? []) as Array<{
      filteringAttribute: string;
      attributeValue: unknown;
    }>;
    const docId = filtering.find((f) => f.filteringAttribute === "documentId")
      ?.attributeValue as string | undefined;
    if (docId === DEMO_LIVE_PERSON_ID) {
      return paged([selfPersonDoc()], req.body);
    }
    // usePersonNames (approval flow, rule sets) resolves names by id list.
    const idArray = filtering.find(
      (f) => f.filteringAttribute === "documentIdArray",
    )?.attributeValue as string[] | undefined;
    if (idArray?.length) {
      const docs: unknown[] = [];
      // usePersonNames keeps only docs with a top-level documentType.
      for (const id of idArray) {
        if (id === DEMO_LIVE_PERSON_ID) {
          docs.push({ documentType: "LivePerson", ...selfPersonDoc() });
        }
        const m = DEMO_TEAM.find((t) => t.id === id);
        if (m) docs.push({ documentType: "LivePerson", ...personDoc(m) });
      }
      return paged(docs, req.body);
    }
    // Person lists are scoped to a company via a nested role filter (contact-
    // person pickers, the team list). Honour it so only that company's members
    // come back — otherwise non-members render with their id as the name.
    const livePersonRead = readDocuments.LivePerson as
      | { filtering?: unknown }
      | undefined;
    const companyId = extractCompanyIdFromFilter(livePersonRead?.filtering);
    let members = (
      companyId
        ? DEMO_TEAM.filter((m) =>
            m.memberships.some((mem) => mem.companyId === companyId),
          )
        : DEMO_TEAM
    ).filter(hasLiveRole);
    const search = String(
      filtering.find((f) => f.filteringAttribute === "substringSearch")
        ?.attributeValue ?? "",
    ).toLowerCase();
    if (search) {
      members = members.filter((m) =>
        `${m.firstName} ${m.surname} ${m.email}`.toLowerCase().includes(search),
      );
    }
    const people = members.map(personDoc);
    const filtered = docId
      ? people.filter((p) => p.documentHeader.id === docId)
      : people;
    return paged(filtered, req.body);
  }
  if ("LiveCompany" in readDocuments || "Company" in readDocuments) {
    return paged(
      DEMO_COMPANIES.map((c) => ({
        documentHeader: { id: c.id, documentType: "LiveCompany" },
        companyBasicInfo: {
          entityData: {
            companyName: c.name,
            registrationNumber: c.registrationNumber,
            vatNumber: c.vatNumber,
            currency: c.defaultCurrency,
            country: c.jurisdiction,
            imgSrc: c.imgSrc,
          },
        },
      })),
      req.body,
    );
  }
  return emptyDocList();
}

function partyReadEntities(req: MockRequest): unknown {
  const documentType = req.body?.documentType as string | undefined;
  const documentRef = req.body?.documentReference as
    | { documentId?: string; documentType?: string }
    | undefined;
  const singleDocId = documentRef?.documentId;

  if (documentType === "Person" || documentType === "LivePerson") {
    if (singleDocId) {
      const found = DEMO_TEAM.find((m) => m.id === singleDocId) ?? DEMO_TEAM[0];
      return livePersonDoc(found);
    }
    return { entities: DEMO_TEAM.map(livePersonDoc) };
  }

  if (
    documentType === "Company" ||
    documentType === "ClientCompany" ||
    documentType === "LiveCompany"
  ) {
    if (singleDocId) {
      const company =
        DEMO_COMPANY_BY_ID[singleDocId as keyof typeof DEMO_COMPANY_BY_ID];
      if (company) return liveCompanyDoc(company);
    }
    return { entities: DEMO_COMPANIES.map(liveCompanyDoc) };
  }
  return { entities: [] };
}

function livePersonDoc(m: DemoTeamMember) {
  return {
    documentHeader: { id: m.id, documentType: "LivePerson" },
    entityData: {
      personBasicInfo: {
        firstName: m.firstName,
        surname: m.surname,
        avatarUrl: m.avatarUrl,
      },
      partyId: m.id,
      email: m.email,
    },
  };
}

// Shape required by useCompanyDetailQuery → mapCompanyData. Reads:
//   companyBasicInfo.entityData.companyName        → legalName
//   partyBasicInfo.entityData.partyName            → name (display)
//   identifiers.returnedItems[].entityData         → IČO etc.
//   contacts.returnedItems[].entityData            → address
//   roles.returnedItems[].entityData.roleBasicInfo → team list
function liveCompanyDoc(c: (typeof DEMO_COMPANIES)[number]) {
  const teamForC = DEMO_TEAM.filter(
    (m) => m.memberships.some((mm) => mm.companyId === c.id) && hasLiveRole(m),
  );
  return {
    documentHeader: { id: c.id, documentType: "LiveCompany" },
    partyBasicInfo: {
      entityData: {
        partyName: c.shortName,
        partyType: "Company",
      },
    },
    companyBasicInfo: {
      entityData: {
        companyName: c.name,
        registrationCountry: c.jurisdiction,
        currency: c.defaultCurrency,
        establishedDate: c.founded,
        description: c.description,
        imgSrc: c.imgSrc,
      },
    },
    identifiers: {
      returnedItems: [
        {
          documentHeader: {
            id: `${c.id}-ico`,
            documentType: "PartyIdentifier",
          },
          entityData: {
            partyIdentifierType: "Ico",
            partyIdentifierValue: c.registrationNumber,
          },
        },
        {
          documentHeader: {
            id: `${c.id}-vat`,
            documentType: "PartyIdentifier",
          },
          entityData: {
            partyIdentifierType: "Vat",
            partyIdentifierValue: c.vatNumber,
          },
        },
      ],
    },
    contacts: {
      returnedItems: [
        {
          documentHeader: { id: `${c.id}-address`, documentType: "Contact" },
          entityData: {
            contactType: "Address",
            address: {
              street: c.street,
              city: c.city,
              postalCode: c.zip,
              country: c.jurisdiction,
            },
          },
        },
      ],
    },
    roles: {
      returnedItems: [
        // The company's own Client role under the service provider. This is what
        // getCompanyEntityId looks up (roleType "Client" + masterRoleDisplayInfo
        // matching the service provider name) to resolve companyEntityId, which
        // in turn ENABLES the team list query. Without it the team query stays
        // disabled and the Team tab / "N users" count render empty.
        {
          documentHeader: {
            id: `${c.id}-client-role`,
            documentType: "PartyRole",
          },
          entityHeader: { entityId: `${c.id}-client` },
          entityData: {
            roleBasicInfo: {
              entityData: {
                roleType: "Client",
                roleName: c.name,
                masterRoleDisplayInfo: {
                  roleType: "ServiceProvider",
                  roleName: import.meta.env.VITE_SERVICE_PROVIDER_PARTY_NAME,
                },
              },
            },
          },
        },
        ...teamForC.map((m) => ({
          documentHeader: { id: m.partyRoleId, documentType: "PartyRole" },
          // getCompanyEntityId + companyTeamList read role.entityData
          // .roleBasicInfo.entityData.roleType — the second `entityData`
          // wrap matters: roleBasicInfo?.entityData.roleType throws when
          // roleBasicInfo exists but its entityData doesn't.
          entityHeader: { entityId: m.partyRoleId },
          entityData: {
            roleBasicInfo: {
              entityData: {
                roleType: "ClientUser",
                roleName: `${m.firstName} ${m.surname}`,
                jobTitle: m.jobTitle,
                jobTitleCustom: m.jobTitleCustom,
                masterRoleRef: {
                  documentId: c.id,
                  documentType: "LiveCompany",
                },
              },
            },
            partyRoleReference: {
              partyId: m.id,
              partyRoleId: m.partyRoleId,
              partyRoleType: "ClientUser",
            },
          },
        })),
      ],
    },
  };
}

function buildPartyRoleRelations() {
  return {
    entities: DEMO_COMPANIES.map((c, i) => ({
      documentHeader: {
        id: `demo-rel-${i}`,
        documentType: "FEAppPartyRoleRelation",
      },
      entityData: {
        masterRoleId: `${c.id}-master`,
        masterRoleName: c.name,
        masterRoleType: "ClientCompany",
        companyRegistrationNumber: c.registrationNumber,
        userPartyRoleId: `demo-role-client-anna-${c.id.split("-").pop()}`,
        isFavourite: i === 0,
        lastUsedAt: "2026-05-21T08:00:00.000Z",
      },
    })),
  };
}

function readEntitiesUserPrefs() {
  // Shape mirrors a UserPreferences readEntities response. A top-level
  // documentHeader is REQUIRED — useUpdateUserPreferences builds its
  // updateDocumentReference from it, and without it the dashboard
  // filter-save mutation throws before sending. dashboardSettings echoes the
  // in-session prefs so currency/range survive across reads.
  return {
    documentHeader: {
      id: "demo-prefs-1",
      documentType: "UserPreferences",
      _ts: Math.floor(Date.now() / 1000),
      _etag: '"demo-etag"',
    },
    userBasicInfo: { entityData: { language: getDemoLanguage() } },
    languagePrefs: { entityData: { language: getDemoLanguage() } },
    currencySettings: {
      entityData: { currency: demoDashboardPrefs.currency },
    },
    ...(demoCashflowWidgetPrefs && {
      widgetsSettings: {
        entityData: { cashflowWidget: demoCashflowWidgetPrefs },
      },
    }),
    dashboardSettings: {
      entityData: {
        range: demoDashboardPrefs.range,
        currency: demoDashboardPrefs.currency,
        selectedCompanies: demoDashboardPrefs.selectedCompanies ?? [],
      },
    },
    ...(demoForecastPrefs.size > 0 && {
      analyticsForecastSettings: {
        pagingResponse: { totalItemsCount: demoForecastPrefs.size },
        returnedItems: [...demoForecastPrefs.entries()].map(
          ([companyId, forecastEnabled]) => ({
            entityHeader: {
              entityType: "AnalyticsForecastSettings",
              definitionVersion: "1",
              entityId: `demo-forecast-${companyId}`,
              entityContext: "UserPreferences",
            },
            entityData: { companyId, forecastEnabled },
          }),
        ),
      },
    }),
    // Per-company analytics settings — interval ONLY (no category split, so the
    // category sections stay at the product default). Persists the Week/Month
    // toggle so Month view stays monthly while paging through periods.
    analyticsDashboardSettings: {
      pagingResponse: { totalItemsCount: DEMO_COMPANIES.length },
      returnedItems: DEMO_COMPANIES.map((c) => ({
        entityHeader: {
          entityType: "AnalyticsDashboardSettings",
          definitionVersion: "1",
          entityId: `demo-analytics-${c.id}`,
          entityContext: "UserPreferences",
        },
        entityData: {
          companyId: c.id,
          aggregationIntervalGranularity:
            demoAnalyticsIntervalPrefs.get(c.id) ?? "1W",
        },
      })),
    },
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Cashflow & historical balance — derived from real transactions
// ────────────────────────────────────────────────────────────────────────────

// Conversion rates to EUR. Symmetric — divide to convert from EUR back to the
// other currency. Realistic mid-2026 EUR rates.
// Also covers the local currencies of EU banks added through the connect flow.
function rateToEur(currency: DemoAccountCurrency | string): number {
  switch (currency) {
    case "HUF":
      return 1 / 395;
    case "RON":
      return 1 / 5;
    case "SEK":
      return 1 / 11.2;
    case "DKK":
      return 1 / 7.46;
    case "NOK":
      return 1 / 11.6;
    case "EUR":
      return 1;
    case "CZK":
      return 1 / 25;
    case "PLN":
      return 1 / 4.3;
    case "USD":
      return 1 / 1.08;
    case "GBP":
      return 1.17;
    default:
      return 1;
  }
}

// Convert a native-currency amount into every supported currency, producing the
// `amount{CURRENCY}` map the transactions SDK exposes under
// `amounts.entityData.amounts`. The drill-down popover reads
// `amounts.amount{viewCurrency}.amount`; using the same rateToEur conversion as
// the cash-flow aggregation guarantees the popover total equals the table cell.
const SUPPORTED_AMOUNT_CURRENCIES: DemoAccountCurrency[] = [
  "EUR",
  "CZK",
  "PLN",
  "GBP",
  "USD",
];
function transactionAmountsByCurrency(
  amount: number,
  currency: DemoAccountCurrency,
): Record<string, { amount: number; currency: DemoAccountCurrency }> {
  const eur = amount * rateToEur(currency);
  const out: Record<string, { amount: number; currency: DemoAccountCurrency }> =
    {};
  for (const target of SUPPORTED_AMOUNT_CURRENCIES) {
    const value = target === currency ? amount : eur / rateToEur(target);
    out[`amount${target}`] = { amount: Math.round(value), currency: target };
  }
  return out;
}

// ────────────────────────────────────────────────────────────────────────────
// Stateful dashboard preferences
//
// The dashboard balance chart does NOT pass currency/range in the request in
// dashboard/company mode — it persists them via user-preferences and the
// backend reads them back into the historical-balance response. To make the
// demo's filters actually take effect (instead of looking hardcoded), we keep
// the last-saved preferences in module state and feed them back.
// ────────────────────────────────────────────────────────────────────────────

type DemoCompanySelection = {
  companyId: string;
  selectedAccounts?: string[];
};

const demoDashboardPrefs: {
  currency: DemoAccountCurrency;
  range: number;
  selectedCompanies: DemoCompanySelection[] | undefined;
} = { currency: "EUR", range: 90, selectedCompanies: undefined };

// Dashboard Summary widget settings (widgetsSettings.cashflowWidget). Absent
// until the user changes a filter/sort — the FE falls back to its defaults.
let demoCashflowWidgetPrefs: Record<string, unknown> | undefined;

// Analytics forecast on/off per company (CAS-1533 toggle). Absent = enabled.
// Session-persisted so the choice survives a reload.
const FORECAST_PREFS_KEY = "fidoo-demo-forecast-prefs-v1";
const demoForecastPrefs = new Map<string, boolean>(
  (() => {
    try {
      return Object.entries(
        JSON.parse(window.sessionStorage.getItem(FORECAST_PREFS_KEY) ?? "{}") as Record<
          string,
          boolean
        >,
      );
    } catch {
      return [];
    }
  })(),
);

const demoCompanyPrefs = new Map<
  string,
  {
    currency: DemoAccountCurrency;
    range: number;
    selectedAccounts: string[] | undefined;
  }
>();

// Per-company analytics aggregation interval ("1W" | "1M"). The cockpit's
// Week/Month toggle persists here (settings.aggregationIntervalGranularity);
// without it `interval` resets to "1W" on every refetch, so paging in Month
// view silently snaps back to weekly. NOTE: this intentionally carries ONLY the
// interval — no `category` — so the two category sections keep the product
// default (all categories) and don't render as an applied filter.
const demoAnalyticsIntervalPrefs = new Map<string, "1W" | "1M">();

function saveDemoDashboardPrefs(req: MockRequest) {
  const body = (req.body ?? {}) as Record<string, unknown>;
  // The SDK sends the entity under `updateEntity` / `createEntity` (not
  // `updatedEntity`) — see useUpdateUserPreferences / useCreateUserPreferencesEntity.
  const entity = (body.updateEntity ??
    body.createEntity ??
    body.updatedEntity ??
    {}) as Record<string, { entityData?: Record<string, unknown> }>;

  // Persist a language switch so it survives prefs refetches and the full-page
  // reloads of the bank-connect redirect (stands in for server-side storage).
  // Only `languagePrefs` carries an explicit user switch; `userBasicInfo` is
  // synced from MSAL claims (default "en") and would otherwise clobber the
  // chosen language on boot.
  const language = entity.languagePrefs?.entityData?.language;
  if (typeof language === "string" && language) {
    setDemoLanguage(language);
  }

  // Profile settings save the primary currency under `currencySettings`
  // (useUserCurrencySettings), not `dashboardSettings`. Persist it to the same
  // in-session store so readEntitiesUserPrefs reflects it after refetch.
  const currencyPref = entity.currencySettings?.entityData;
  if (currencyPref && typeof currencyPref.currency === "string") {
    demoDashboardPrefs.currency = currencyPref.currency as DemoAccountCurrency;
  }

  const dash = entity.dashboardSettings?.entityData;
  if (dash) {
    if (typeof dash.currency === "string") {
      demoDashboardPrefs.currency = dash.currency as DemoAccountCurrency;
    }
    if (dash.range != null) demoDashboardPrefs.range = Number(dash.range);
    // Only persist a non-empty selection. The dashboard form can fire an
    // initial save before account data has loaded; storing that empty array
    // would make every later balance query sum zero accounts (→ €0 / flaky).
    if (
      Array.isArray(dash.selectedCompanies) &&
      dash.selectedCompanies.length > 0
    ) {
      demoDashboardPrefs.selectedCompanies =
        dash.selectedCompanies as DemoCompanySelection[];
    }
  }

  const forecast = entity.analyticsForecastSettings?.entityData;
  if (forecast && typeof forecast.companyId === "string") {
    demoForecastPrefs.set(forecast.companyId, forecast.forecastEnabled !== false);
    try {
      window.sessionStorage.setItem(
        FORECAST_PREFS_KEY,
        JSON.stringify(Object.fromEntries(demoForecastPrefs)),
      );
    } catch {
      // ignore
    }
  }

  const widget = entity.widgetsSettings?.entityData?.cashflowWidget;
  if (widget && typeof widget === "object") {
    demoCashflowWidgetPrefs = {
      ...demoCashflowWidgetPrefs,
      ...(widget as Record<string, unknown>),
    };
  }

  const comp = entity.companyDashboardSettings?.entityData;
  if (comp && typeof comp.companyId === "string") {
    demoCompanyPrefs.set(comp.companyId, {
      currency:
        typeof comp.currency === "string"
          ? (comp.currency as DemoAccountCurrency)
          : "EUR",
      range: comp.range != null ? Number(comp.range) : 90,
      selectedAccounts: Array.isArray(comp.selectedAccounts)
        ? (comp.selectedAccounts as string[])
        : undefined,
    });
  }

  // Persist the Week/Month toggle so Month view survives refetches and period
  // navigation (otherwise interval resets to "1W" and paging reverts to weekly).
  const analytics = entity.analyticsDashboardSettings?.entityData;
  if (analytics && typeof analytics.companyId === "string") {
    const granularity = analytics.aggregationIntervalGranularity;
    if (granularity === "1W" || granularity === "1M") {
      demoAnalyticsIntervalPrefs.set(analytics.companyId, granularity);
    }
  }

  return { documentId: "demo-pref-new" };
}

// The cash-flow + balance charts and the cockpit cashflow table are all driven
// by aggregating DEMO_TRANSACTIONS (converted to the requested currency). This
// keeps the chart, the per-category table and the category drill-down popover
// perfectly consistent — every number traces back to the same seeded
// transactions. The seed (seed/transactions.ts) generates a dense, near-daily,
// healthy stream so the aggregated view reads as a busy, profitable business.
function buildHistoricalBalance(
  req: MockRequest,
): DeepPartial<HistoricalBalanceResponse> {
  const filtering = (req.body?.filtering ?? {}) as Record<string, unknown>;
  const companyFilter = filtering.companyFilter as
    | { companyId: string; selectedAccounts?: string[] }
    | { companyId: string; selectedAccounts?: string[] }[]
    | undefined;

  // Company-detail mode scopes the chart to one company. The FE sends
  // companyFilter as a single-item ARRAY ([{ companyId }] — see
  // resolveCompanyFilter in BalanceChartContext); dashboard mode sends no
  // companyFilter at all. Accept both the array and the bare-object shape.
  const singleCompanyId = Array.isArray(companyFilter)
    ? companyFilter.length === 1
      ? companyFilter[0].companyId
      : undefined
    : companyFilter?.companyId;
  const companyPref = singleCompanyId
    ? demoCompanyPrefs.get(singleCompanyId)
    : undefined;

  const currency = ((filtering.currency as DemoAccountCurrency | undefined) ??
    companyPref?.currency ??
    demoDashboardPrefs.currency ??
    "EUR") as DemoAccountCurrency;
  const range = Math.max(
    Number(
      filtering.range ?? companyPref?.range ?? demoDashboardPrefs.range ?? 90,
    ),
    7,
  );

  const companies: DemoCompanySelection[] = Array.isArray(companyFilter)
    ? companyFilter
    : companyFilter
      ? [companyFilter]
      : demoDashboardPrefs.selectedCompanies?.length
        ? demoDashboardPrefs.selectedCompanies
        : DEMO_COMPANIES.map((c) => ({ companyId: c.id }));

  // Compute current total balance across selected accounts, expressed in the
  // request currency.
  const selectedAccounts: DemoAccount[] = companies.flatMap((cf) => {
    const companyAccounts = activeDemoAccounts().filter(
      (a) => a.companyId === cf.companyId,
    );
    if (cf.selectedAccounts && cf.selectedAccounts.length > 0) {
      const set = new Set(cf.selectedAccounts);
      const matched = companyAccounts.filter((a) => set.has(a.id));
      // Fall back to all of the company's accounts when the selection matches
      // nothing — otherwise a stale/mismatched account filter makes the demo
      // show a €0 balance (which looks broken).
      return matched.length > 0 ? matched : companyAccounts;
    }
    return companyAccounts;
  });

  const finalBalance = selectedAccounts.reduce(
    (sum, a) => sum + a.balance * (rateToEur(a.currency) / rateToEur(currency)),
    0,
  );

  // Aggregate per-day income / costs from real transactions on the selected
  // accounts, converted to the requested currency. Driving the chart from
  // DEMO_TRANSACTIONS (rather than a synthetic series) keeps the balance line
  // consistent with the cockpit cashflow numbers and the transaction lists.
  const accountSet = new Set(selectedAccounts.map((a) => a.id));
  const byDay = new Map<string, { income: number; costs: number }>();
  for (const t of DEMO_TRANSACTIONS) {
    if (!accountSet.has(t.accountId)) continue;
    const day = t.bookedAt.slice(0, 10);
    const conv = t.amount * (rateToEur(t.currency) / rateToEur(currency));
    const bucket = byDay.get(day) ?? { income: 0, costs: 0 };
    if (conv > 0) bucket.income += conv;
    else bucket.costs += -conv;
    byDay.set(day, bucket);
  }

  // Walk back from the real "today" inserting one row per day. Balance for day
  // D = balance for D+1 - income[D+1] + costs[D+1] — i.e. unwind the flows that
  // happened on the next day, so the line gently rises to finalBalance.
  const days: {
    timestamp: string;
    income: number;
    costs: number;
    balance: number;
    balanceQuality: "IMPORTED";
  }[] = [];
  let running = finalBalance;
  const today = new Date();
  for (let i = 0; i < range; i++) {
    const d = new Date(today);
    d.setUTCDate(d.getUTCDate() - i);
    const stamp = d.toISOString().slice(0, 10);
    const flow = byDay.get(stamp) ?? { income: 0, costs: 0 };
    days.unshift({
      timestamp: stamp,
      income: Math.round(flow.income),
      costs: Math.round(flow.costs),
      balance: Math.round(running),
      balanceQuality: "IMPORTED",
    });
    running -= flow.income - flow.costs;
  }

  return {
    currency,
    originalCurrency: currency,
    timestamp: days[days.length - 1].timestamp,
    finalBalance: Math.round(finalBalance),
    finalBalanceInOriginalCurrency: Math.round(finalBalance),
    balanceQuality: "IMPORTED",
    income: days.reduce((s, d) => s + d.income, 0),
    costs: days.reduce((s, d) => s + d.costs, 0),
    maxLookBackDate: days[0].timestamp,
    historicalBalance: days,
    dashboardSettings: {
      range,
      selectedCompanies: companies.map((cf) => ({
        companyId: cf.companyId,
        selectedAccounts:
          cf.selectedAccounts ??
          activeDemoAccounts().filter((a) => a.companyId === cf.companyId).map(
            (a) => a.id,
          ),
      })),
    },
    // Company-detail mode reads companyDashboardSettings instead — provide it so
    // that chart's range/currency also reflect the saved company prefs.
    ...(singleCompanyId
      ? {
          companyDashboardSettings: {
            companyId: singleCompanyId,
            range,
            // No saved company selection → default to ALL of the company's
            // accounts, like the real BE. The FE normalizes undefined to []
            // (explicit empty selection), which unchecks every account in
            // the filter and hides the count.
            selectedAccounts:
              companyPref?.selectedAccounts ??
              activeDemoAccounts().filter(
                (a) => a.companyId === singleCompanyId,
              ).map((a) => a.id),
          },
        }
      : {}),
  };
}

// Shape matches GetCashFlowAndBalanceStatesFunctionModel — the Cockpit page
// builds the current-balance area chart from aggregatedBalanceValues[] and
// the cashflow bar chart from cashFlowStates.sumArray[].
// The forecast request carries no currency (the backend resolves it from the
// analytics view); reuse the one the realized cash-flow query just used so the
// forecast columns line up with the realized ones.
let lastCashflowCurrency: DemoAccountCurrency = "EUR";

// Cash-flow forecast (CAS-1533): projects the selected accounts' average
// weekly / monthly income and costs from the last 90 days forward to dateTo.
// Period keys mirror buildCashFlowAndBalanceStates' bucketKey so the current
// period merges with its realized column.
function buildCashflowForecast(
  req: MockRequest,
): BwCashflowModel["getForecast"]["response"] {
  const f = (req.body?.filtering ?? {}) as {
    dateTo?: string;
    bankAccountIdArray?: string[];
    sumAggregationIntervalGranularity?: "1D" | "1W" | "1M";
    currency?: DemoAccountCurrency;
  };
  const granularity = f.sumAggregationIntervalGranularity === "1M" ? "1M" : "1W";
  const currency = f.currency ?? lastCashflowCurrency;
  const accountSet = new Set(f.bankAccountIdArray ?? []);
  const accounts = DEMO_ACCOUNTS.filter((a) => accountSet.has(a.id));
  const convert = (amount: number, from: DemoAccountCurrency) =>
    (amount * rateToEur(from)) / rateToEur(currency);
  const DAY = 86_400_000;
  const todayStamp = new Date().toISOString().slice(0, 10);
  const today = new Date(`${todayStamp}T00:00:00.000Z`);
  const lookbackFrom = new Date(today.getTime() - 90 * DAY)
    .toISOString()
    .slice(0, 10);

  let income90 = 0;
  let costs90 = 0;
  const realizedByPeriod = new Map<string, { income: number; costs: number }>();
  const periodKey = (stamp: string) => {
    if (granularity === "1M") return stamp.slice(0, 7);
    const d = new Date(`${stamp}T00:00:00.000Z`);
    const dow = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
    return new Date(d.getTime() - (dow - 1) * DAY).toISOString().slice(0, 10);
  };
  for (const t of DEMO_TRANSACTIONS) {
    if (!accountSet.has(t.accountId)) continue;
    const day = t.bookedAt.slice(0, 10);
    if (day < lookbackFrom || day > todayStamp) continue;
    const conv = convert(t.amount, t.currency);
    if (conv > 0) income90 += conv;
    else costs90 += -conv;
    const key = periodKey(day);
    const cur = realizedByPeriod.get(key) ?? { income: 0, costs: 0 };
    if (conv > 0) cur.income += conv;
    else cur.costs += -conv;
    realizedByPeriod.set(key, cur);
  }

  const periodDays = granularity === "1M" ? 30.4 : 7;
  const avgIncome = (income90 / 90) * periodDays;
  const avgCosts = (costs90 / 90) * periodDays;
  const currentBalance = accounts.reduce(
    (acc, a) => acc + convert(a.balance, a.currency),
    0,
  );

  if (!accounts.length || income90 + costs90 === 0) {
    return {
      aggregatedBalanceForecastValues: [],
      cashFlowForecastStates: [],
      forecastStatus: "NOT_ENOUGH_DATA",
    };
  }

  const dateTo = f.dateTo ?? todayStamp;
  const periods: { key: string; from: Date; to: Date }[] = [];
  let cursor = new Date(
    `${granularity === "1M" ? `${todayStamp.slice(0, 7)}-01` : periodKey(todayStamp)}T00:00:00.000Z`,
  );
  while (cursor.toISOString().slice(0, 10) <= dateTo) {
    const from = new Date(cursor);
    const to =
      granularity === "1M"
        ? new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + 1, 0))
        : new Date(from.getTime() + 6 * DAY);
    periods.push({ key: periodKey(from.toISOString().slice(0, 10)), from, to });
    cursor =
      granularity === "1M"
        ? new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + 1, 1))
        : new Date(from.getTime() + 7 * DAY);
  }

  const round = (n: number) => Math.round(n);
  // Gentle deterministic wobble so the projection doesn't look flat.
  const wobble = (i: number, phase: number) => 1 + 0.07 * Math.sin(i * 1.7 + phase);
  let balance = currentBalance;
  const aggregatedBalanceForecastValues: BwCashflowModel["getForecast"]["response"]["aggregatedBalanceForecastValues"] =
    [];
  const cashFlowForecastStates = periods.map((p, i) => {
    const realized = realizedByPeriod.get(p.key) ?? { income: 0, costs: 0 };
    const income = Math.max(realized.income, avgIncome * wobble(i, 0.3));
    const costs = Math.max(realized.costs, avgCosts * wobble(i, 1.1));
    const startedBalance = i === 0 ? currentBalance - (realized.income - realized.costs) : balance;
    const finalBalance = startedBalance + income - costs;
    // Daily balance points for the not-yet-elapsed days of this period.
    const days = Math.round((p.to.getTime() - p.from.getTime()) / DAY) + 1;
    for (let d = 0; d < days; d++) {
      const date = new Date(p.from.getTime() + d * DAY);
      const stamp = date.toISOString().slice(0, 10);
      if (stamp <= todayStamp || stamp > dateTo) continue;
      const progress = (d + 1) / days;
      aggregatedBalanceForecastValues.push({
        date: stamp,
        balance: round(startedBalance + (income - costs) * progress),
        balanceQuality: "PREDICTED",
      });
    }
    balance = finalBalance;
    return {
      startedBalance: {
        balance: round(startedBalance),
        date: p.from.toISOString().slice(0, 10),
        balanceQuality: i === 0 ? ("IMPORTED" as const) : ("PREDICTED" as const),
      },
      finalBalance: {
        balance: round(finalBalance),
        date: p.to.toISOString().slice(0, 10),
        balanceQuality: "PREDICTED" as const,
      },
      income: round(income),
      costs: round(costs),
      netChange: round(income - costs),
      dateFrom: p.key,
      dateTo: p.to.toISOString().slice(0, 10),
    };
  });

  return {
    aggregatedBalanceForecastValues,
    cashFlowForecastStates,
    forecastStatus: "AVAILABLE",
  };
}

function buildCashFlowAndBalanceStates(req: MockRequest) {
  const filtering = (req.body?.filtering ?? {}) as {
    currency?: DemoAccountCurrency;
    dateFrom?: string;
    dateTo?: string;
    bankAccountIdArray?: string[];
    returnBalanceStates?: boolean;
    returnTransactionCount?: boolean;
    cumulativeSums?: boolean;
    sumAggregationIntervalGranularity?: "1D" | "1W" | "1M";
    direction?: "IN" | "OUT";
  };

  const currency = (filtering.currency ?? "EUR") as DemoAccountCurrency;
  lastCashflowCurrency = currency;
  const accountIds =
    filtering.bankAccountIdArray && filtering.bankAccountIdArray.length > 0
      ? filtering.bankAccountIdArray
      : activeDemoAccounts().map((a) => a.id);
  const accountSet = new Set(accountIds);
  const dateTo = filtering.dateTo ?? new Date().toISOString().slice(0, 10);
  const dateFrom =
    filtering.dateFrom ??
    new Date(Date.now() - 90 * 24 * 3600_000).toISOString().slice(0, 10);
  // The cockpit doesn't send a granularity — the BE derives it (and the
  // week/month toggle just changes the date-range width). Mirror that: pick the
  // bucket size from the window width so the toggle works and the table stays
  // readable (~one column per bucket, not 90 daily columns). Values are the
  // real API enum "1D"/"1W"/"1M".
  const rangeDays = Math.max(
    1,
    Math.round(
      (new Date(dateTo + "T00:00:00.000Z").getTime() -
        new Date(dateFrom + "T00:00:00.000Z").getTime()) /
        86_400_000,
    ),
  );
  // Window widths the cockpit produces: ~columnCount weeks (weekly toggle) or
  // ~columnCount months (monthly toggle). Map width → bucket size so the weekly
  // default (~6–15 weeks) groups by week and the monthly toggle (~6–15 months)
  // groups by month; only very narrow custom ranges fall back to daily.
  const granularity: "1D" | "1W" | "1M" =
    filtering.sumAggregationIntervalGranularity ??
    (rangeDays >= 120 ? "1M" : rangeDays >= 14 ? "1W" : "1D");

  const selectedAccounts = DEMO_ACCOUNTS.filter((a) => accountSet.has(a.id));
  const healthCompanyId = selectedAccounts[0]?.companyId;
  const since90 = new Date(Date.now() - 90 * 86_400_000).toISOString().slice(0, 10);
  const costsLast90Days = DEMO_TRANSACTIONS.reduce((sum, t) => {
    if (!accountSet.has(t.accountId) || t.amount >= 0) return sum;
    if (t.bookedAt.slice(0, 10) < since90) return sum;
    return sum + -t.amount * (rateToEur(t.currency) / rateToEur(currency));
  }, 0);

  // Final balance across selected accounts in the request currency.
  const finalBalance = selectedAccounts.reduce(
    (sum, a) => sum + a.balance * (rateToEur(a.currency) / rateToEur(currency)),
    0,
  );

  // Daily income/costs from real transactions on the selected accounts,
  // converted to the requested currency. Aggregating DEMO_TRANSACTIONS (instead
  // of a synthetic series) makes every cashflow-table cell equal to the sum of
  // the transactions the category drill-down lists for that period+category, so
  // the chart, the category table and the popover all agree.
  type DayFlow = {
    income: number;
    costs: number;
    incomeByCategory: Map<string, number>;
    costsByCategory: Map<string, number>;
    txCount: number;
  };
  const byDay = new Map<string, DayFlow>();
  for (const t of DEMO_TRANSACTIONS) {
    if (!accountSet.has(t.accountId)) continue;
    const day = t.bookedAt.slice(0, 10);
    if (day < dateFrom || day > dateTo) continue;
    const conv = t.amount * (rateToEur(t.currency) / rateToEur(currency));
    const bucket: DayFlow = byDay.get(day) ?? {
      income: 0,
      costs: 0,
      incomeByCategory: new Map(),
      costsByCategory: new Map(),
      txCount: 0,
    };
    if (conv > 0) {
      bucket.income += conv;
      bucket.incomeByCategory.set(
        t.category,
        (bucket.incomeByCategory.get(t.category) ?? 0) + conv,
      );
    } else {
      bucket.costs += -conv;
      bucket.costsByCategory.set(
        t.category,
        (bucket.costsByCategory.get(t.category) ?? 0) + -conv,
      );
    }
    bucket.txCount += 1;
    byDay.set(day, bucket);
  }

  // Build aggregatedBalanceValues — daily balance derived backwards from
  // finalBalance, same logic as getHistoricalBalance but inside this response.
  const aggregatedBalanceValues: Array<{
    balance: number;
    date: string;
    balanceQuality: string;
    accounts?: Array<{
      bic?: string;
      accountName: string;
      balance: number;
      balanceQuality: string;
      originalCurrency: DemoAccountCurrency;
      originalCurrencyBalance: number;
    }>;
  }> = [];
  let running = finalBalance;
  const today = new Date(dateTo + "T00:00:00.000Z");
  const start = new Date(dateFrom + "T00:00:00.000Z");
  const totalDays = Math.max(
    1,
    Math.round((today.getTime() - start.getTime()) / (24 * 3600_000)) + 1,
  );
  for (let i = 0; i < totalDays; i++) {
    const d = new Date(today);
    d.setUTCDate(d.getUTCDate() - i);
    const stamp = d.toISOString().slice(0, 10);
    const flow = byDay.get(stamp) ?? { income: 0, costs: 0 };
    aggregatedBalanceValues.unshift({
      date: stamp,
      balance: Math.round(running),
      balanceQuality: "IMPORTED",
      accounts: selectedAccounts.map((a) => {
        const acctShare =
          a.balance * (rateToEur(a.currency) / rateToEur(currency));
        return {
          bic: DEMO_BANKS[a.bankKey].bic,
          accountName: `${a.name} · ${DEMO_BANKS[a.bankKey].name}`,
          balance: Math.round(
            acctShare * (running / Math.max(1, finalBalance)),
          ),
          balanceQuality: "IMPORTED",
          originalCurrency: a.currency,
          originalCurrencyBalance: a.balance,
        };
      }),
    });
    running -= flow.income - flow.costs;
  }

  // Build cashFlowStates.sumArray — group by granularity (1D/1W/1M).
  function bucketKey(stamp: string): string {
    if (granularity === "1M") return stamp.slice(0, 7);
    if (granularity === "1W") {
      const d = new Date(stamp + "T00:00:00.000Z");
      const weekStart = new Date(d);
      // Week starts Monday (getUTCDay: 0=Sun → treat as 7).
      const dow = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
      weekStart.setUTCDate(d.getUTCDate() - (dow - 1));
      return weekStart.toISOString().slice(0, 10);
    }
    return stamp;
  }

  const intervalBuckets = new Map<string, DayFlow>();
  for (const [day, flow] of byDay) {
    const key = bucketKey(day);
    const bucket: DayFlow = intervalBuckets.get(key) ?? {
      income: 0,
      costs: 0,
      incomeByCategory: new Map(),
      costsByCategory: new Map(),
      txCount: 0,
    };
    bucket.income += flow.income;
    bucket.costs += flow.costs;
    for (const [k, v] of flow.incomeByCategory) {
      bucket.incomeByCategory.set(k, (bucket.incomeByCategory.get(k) ?? 0) + v);
    }
    for (const [k, v] of flow.costsByCategory) {
      bucket.costsByCategory.set(k, (bucket.costsByCategory.get(k) ?? 0) + v);
    }
    bucket.txCount += flow.txCount;
    intervalBuckets.set(key, bucket);
  }

  const sortedBuckets = [...intervalBuckets.entries()].sort(([a], [b]) =>
    a < b ? -1 : 1,
  );
  let cumIncome = 0;
  let cumCosts = 0;
  const sumArray = sortedBuckets.map(([key, flow]) => {
    cumIncome += flow.income;
    cumCosts += flow.costs;
    const dateFrom = key;
    const dateToVal =
      granularity === "1M"
        ? `${key}-${String(new Date(Number(key.slice(0, 4)), Number(key.slice(5, 7)), 0).getUTCDate()).padStart(2, "0")}`
        : granularity === "1W"
          ? new Date(
              new Date(`${key}T00:00:00.000Z`).getTime() + 6 * 24 * 3600_000,
            )
              .toISOString()
              .slice(0, 10)
          : key;

    return {
      sumAggregationalGranularity: granularity,
      incomeCategorySum: [...flow.incomeByCategory.entries()].map(
        ([category, amount]) => ({
          category,
          amount: Math.round(amount),
          transactionCount: undefined,
          cellId: `${key}-in-${category}`,
        }),
      ),
      costsCategorySum: [...flow.costsByCategory.entries()].map(
        ([category, amount]) => ({
          category,
          amount: Math.round(amount),
          transactionCount: undefined,
          cellId: `${key}-out-${category}`,
        }),
      ),
      costs: Math.round(flow.costs),
      income: Math.round(flow.income),
      costCellId: `${key}-out`,
      incomeCellId: `${key}-in`,
      netChange: Math.round(flow.income - flow.costs),
      cumulativeCosts: Math.round(cumCosts),
      cumulativeIncome: Math.round(cumIncome),
      cumulativeNetChange: Math.round(cumIncome - cumCosts),
      transactionCount: flow.txCount,
      dateFrom,
      dateTo: dateToVal,
    };
  });

  const totalIncome = sortedBuckets.reduce((s, [, f]) => s + f.income, 0);
  const totalCosts = sortedBuckets.reduce((s, [, f]) => s + f.costs, 0);

  return {
    accountBalanceHistoryArray: filtering.returnBalanceStates
      ? selectedAccounts.map((a) => ({
          bankAccountRef: { documentId: a.id },
          balanceStateArray: aggregatedBalanceValues.map((p) => ({
            balance: Math.round(
              a.balance *
                (rateToEur(a.currency) / rateToEur(currency)) *
                (p.balance / Math.max(1, finalBalance)),
            ),
            originalAmount: {
              amount: a.balance,
              currency: a.currency,
              timestamp: p.date,
            },
            timestamp: p.date,
          })),
        }))
      : undefined,
    aggregatedBalanceValues,
    cashFlowStates: {
      sumAggregationalGranularity: granularity,
      sumArray,
      totalSums: {
        costs: Math.round(totalCosts),
        income: Math.round(totalIncome),
        netChange: Math.round(totalIncome - totalCosts),
      },
    },
    currency,
    // The bw-2606 analytics page reads the headline balance from this
    // dedicated field (legendData in CashflowChartContext) — without it
    // "Aktuální zůstatek" renders empty.
    todayAggregatedBalance: {
      balance: Math.round(finalBalance),
      balanceQuality: "IMPORTED",
    },
    // Financial-health gauges (7/14/30 days ahead). healthIndicator is a 0-1
    // cash-reserve fraction; expected costs are projected from the average
    // daily costs in the requested window.
    healthIndicators: [7, 14, 30].map((daysAhead) => {
      // RC10 (CAS-1751): average daily cost over the last 90 days, plus the
      // amount left after expected costs and how many days the balance covers.
      const avgDailyCosts = costsLast90Days / 90;
      const expectedCosts = Math.round(avgDailyCosts * daysAhead);
      const healthIndicator = Math.min(
        1,
        Math.max(0, 1 - expectedCosts / Math.max(1, finalBalance)),
      );
      const { thresholdBad, thresholdGood } =
        (healthCompanyId && getCompanySettings(healthCompanyId).healthThresholds) ||
        DEFAULT_HEALTH_THRESHOLDS;
      return {
        daysAhead: daysAhead as 7 | 14 | 30,
        currentBalance: Math.round(finalBalance),
        expectedCosts,
        healthIndicator,
        thresholdBad,
        thresholdGood,
        remaining: Math.round(finalBalance - expectedCosts),
        averageDailyCost: Math.round(avgDailyCosts),
        coveredDays:
          avgDailyCosts > 0 ? Math.floor(finalBalance / avgDailyCosts) : 0,
        category:
          healthIndicator >= thresholdGood
            ? ("GOOD" as const)
            : healthIndicator >= thresholdBad
              ? ("AVERAGE" as const)
              : ("BAD" as const),
      };
    }),
  };
}

// Dashboard Summary (CAS-1864): inflow / outflow / net change / current
// balance cards plus their breakdown panels, derived from the same seeded
// transactions and accounts as the rest of the demo. Amounts in `summary` are
// in the user's display currency; per-account / per-currency rows stay native.
type WidgetSort = "LATEST_FIRST" | "OLDEST_FIRST" | "LARGEST_FIRST" | "SMALLEST_FIRST";
const WIDGET_TRANSACTION_LIMIT = 10;

function buildCashFlowInsightsWidget(
  req: MockRequest,
): BwCashflowModel["getCashFlowInsightsWidgetData"]["response"] {
  const f = (req.body?.filtering ?? {}) as {
    period?: number;
    companyIdArray?: string[];
    transactionsSort?: WidgetSort;
    netChangeSort?: "LARGEST_FIRST" | "SMALLEST_FIRST";
  };
  const stored = demoCashflowWidgetPrefs ?? {};
  const period = Number(f.period ?? stored.period ?? 7);
  const transactionsSort = (f.transactionsSort ??
    stored.transactionsSort ??
    "LATEST_FIRST") as WidgetSort;
  const netChangeSort = f.netChangeSort ?? "LARGEST_FIRST";
  const companyIdArray = f.companyIdArray ?? [];
  const companyFilter = companyIdArray.length ? new Set(companyIdArray) : null;
  const currency = demoDashboardPrefs.currency;
  const convert = (amount: number, from: DemoAccountCurrency) =>
    from === currency ? amount : (amount * rateToEur(from)) / rateToEur(currency);
  const round = (n: number) => Math.round(n * 100) / 100;

  const now = Date.now();
  const since = now - period * 86_400_000;
  const txs = DEMO_TRANSACTIONS.filter((t) => {
    const ts = new Date(t.bookedAt).getTime();
    return (
      ts >= since &&
      ts <= now &&
      !isArchivedAccountId(t.accountId) &&
      (!companyFilter || companyFilter.has(t.companyId))
    );
  });
  const accounts = activeDemoAccounts().filter(
    (a) => !companyFilter || companyFilter.has(a.companyId),
  );

  const inflowTxs = txs.filter((t) => t.amount > 0);
  const outflowTxs = txs.filter((t) => t.amount < 0);
  const sumConverted = (list: typeof txs) =>
    list.reduce((acc, t) => acc + convert(t.amount, t.currency), 0);
  const inflow = sumConverted(inflowTxs);
  const outflow = sumConverted(outflowTxs);

  const sortTxs = (list: typeof txs) =>
    [...list]
      .sort((a, b) => {
        switch (transactionsSort) {
          case "OLDEST_FIRST":
            return a.bookedAt < b.bookedAt ? -1 : 1;
          case "LARGEST_FIRST":
            return (
              Math.abs(convert(b.amount, b.currency)) -
              Math.abs(convert(a.amount, a.currency))
            );
          case "SMALLEST_FIRST":
            return (
              Math.abs(convert(a.amount, a.currency)) -
              Math.abs(convert(b.amount, b.currency))
            );
          default:
            return a.bookedAt < b.bookedAt ? 1 : -1;
        }
      })
      .slice(0, WIDGET_TRANSACTION_LIMIT)
      .map((t) => {
        const doc = transactionDoc(t);
        // Company colour chips key off the LiveCompany id.
        return {
          ...doc,
          partyRoleRef: {
            entityData: { documentType: "LiveCompany", documentId: t.companyId },
          },
        };
      });

  const flowsBy = <K extends string>(key: (t: (typeof txs)[number]) => K) => {
    const map = new Map<K, { inflow: number; outflow: number }>();
    for (const t of txs) {
      const k = key(t);
      const cur = map.get(k) ?? { inflow: 0, outflow: 0 };
      const amount = t.amount;
      if (amount > 0) cur.inflow += amount;
      else cur.outflow += amount;
      map.set(k, cur);
    }
    return map;
  };

  const isAccountView =
    companyIdArray.length === 1 || DEMO_COMPANIES.length === 1;
  type NetChangeRow = NonNullable<
    BwCashflowModel["getCashFlowInsightsWidgetData"]["response"]["netChange"]
  >[number];
  let netChange: { row: NetChangeRow; sortValue: number }[];
  if (isAccountView) {
    const byAccount = flowsBy((t) => t.accountId);
    netChange = accounts.map((a) => {
      const flows = byAccount.get(a.id) ?? { inflow: 0, outflow: 0 };
      const net = flows.inflow + flows.outflow;
      return {
        sortValue: convert(net, a.currency),
        row: {
          accountName: a.name,
          iban: a.iban,
          connectionStatus: "CONNECTED",
          inflow: round(flows.inflow),
          outflow: round(flows.outflow),
          netChange: round(net),
          currency: a.currency,
        },
      };
    });
  } else {
    netChange = DEMO_COMPANIES.filter(
      (c) => !companyFilter || companyFilter.has(c.id),
    ).map((c) => {
      let inC = 0;
      let outC = 0;
      for (const t of txs) {
        if (t.companyId !== c.id) continue;
        if (t.amount > 0) inC += convert(t.amount, t.currency);
        else outC += convert(t.amount, t.currency);
      }
      return {
        sortValue: inC + outC,
        row: {
          companyName: c.name,
          companyId: c.id,
          accountsCount: accounts.filter((a) => a.companyId === c.id).length,
          inflow: round(inC),
          outflow: round(outC),
          netChange: round(inC + outC),
          currency,
        },
      };
    });
  }
  netChange.sort((a, b) =>
    netChangeSort === "SMALLEST_FIRST"
      ? a.sortValue - b.sortValue
      : b.sortValue - a.sortValue,
  );

  const byCurrency = flowsBy((t) => t.currency);
  const currencies = [...new Set(accounts.map((a) => a.currency))];
  const balance = currencies
    .map((cur) => {
      const accs = accounts.filter((a) => a.currency === cur);
      const flows = byCurrency.get(cur) ?? { inflow: 0, outflow: 0 };
      return {
        currency: cur,
        balance: round(accs.reduce((acc, a) => acc + a.balance, 0)),
        accountsCount: accs.length,
        balanceQuality: "IMPORTED" as const,
        inflow: round(flows.inflow),
        outflow: round(flows.outflow),
        netChange: round(flows.inflow + flows.outflow),
      };
    })
    .sort((a, b) => convert(b.balance, b.currency) - convert(a.balance, a.currency));

  type WidgetResponse = BwCashflowModel["getCashFlowInsightsWidgetData"]["response"];
  return {
    currency: currency as WidgetResponse["currency"],
    summary: {
      inflow: round(inflow),
      inflowTransactionsCount: inflowTxs.length,
      outflow: round(outflow),
      outflowTransactionsCount: outflowTxs.length,
      netChange: round(inflow + outflow),
      transactionsCount: txs.length,
      currentBalance: round(
        accounts.reduce((acc, a) => acc + convert(a.balance, a.currency), 0),
      ),
      currenciesCount: currencies.length,
      balanceQuality: "IMPORTED",
    },
    inflowTransactions: sortTxs(inflowTxs),
    outflowTransactions: sortTxs(outflowTxs),
    netChange: netChange.map((n) => n.row),
    balance,
  };
}

function buildCashflowSummary() {
  const months: Array<{ month: string; income: number; expense: number }> = [];
  const sums = new Map<string, { income: number; expense: number }>();
  for (const t of DEMO_TRANSACTIONS) {
    const m = t.bookedAt.slice(0, 7);
    const conv = t.amount * rateToEur(t.currency);
    const bucket = sums.get(m) ?? { income: 0, expense: 0 };
    if (conv > 0) bucket.income += conv;
    else bucket.expense += -conv;
    sums.set(m, bucket);
  }
  [...sums.entries()].sort().forEach(([month, v]) =>
    months.push({
      month,
      income: Math.round(v.income),
      expense: Math.round(v.expense),
    }),
  );
  return { months, currency: "EUR" };
}

function buildCategoryBreakdown() {
  const totals = new Map<string, number>();
  for (const t of DEMO_TRANSACTIONS) {
    const conv = t.amount * rateToEur(t.currency);
    totals.set(t.category, (totals.get(t.category) ?? 0) + conv);
  }
  const all = [...totals.entries()];
  const totalAbs = all.reduce((s, [, v]) => s + Math.abs(v), 0);
  return {
    period: { from: "2026-02-20", to: "2026-05-21" },
    currency: "EUR",
    categories: all.map(([name, amount]) => ({
      name,
      amount: Math.round(amount),
      share: totalAbs ? Math.abs(amount) / totalAbs : 0,
    })),
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Balances list (FX provider tab) — reuses the same accounts.
// ────────────────────────────────────────────────────────────────────────────

function buildBalances() {
  const docs = activeDemoAccounts().map((a) => ({
    documentHeader: { id: `demo-balance-${a.id}`, documentType: "Balance" },
    entityData: {
      bankAccountId: a.id,
      bankAccountName: a.name,
      currency: a.currency,
      currentBalance: a.balance,
      availableBalance: a.availableBalance,
      asOf: a.lastUpdate,
    },
  }));
  return paged(docs, {});
}

// ────────────────────────────────────────────────────────────────────────────
// Products / marketplace / cards / invoices / exchange rates
// ────────────────────────────────────────────────────────────────────────────

function buildAvailableProducts() {
  return [
    // Keys must match @dfp/bw-react-marketplace translations.products (PascalCase)
    // or the offer cards won't render.
    {
      key: "BusinessInsurance",
      productGroup: "insurance",
      availabilityStatus: "available",
      eligible: true,
    },
    {
      key: "FlowpayCredit",
      productGroup: "financing",
      availabilityStatus: "available",
      eligible: true,
    },
    {
      key: "BibbyFactoring",
      productGroup: "financing",
      availabilityStatus: "available",
      eligible: true,
    },
    // Additional marketplace offers (no productActions route → card button is
    // disabled by design; they exist to flesh out the catalogue).
    {
      key: "malcomOnlineInvoiceFinancing",
      productGroup: "financing",
      availabilityStatus: "available",
      eligible: true,
    },
    {
      key: "rogerRogerPlatba",
      productGroup: "financing",
      availabilityStatus: "available",
      eligible: true,
    },
    // Lemonero hidden for now (per request).
    // NOTE: directPojistovnaFleetInsurance and directAutoFleetPurchase used to
    // be returned here to flesh out the catalogue. Since bw-2606 production
    // renders them as static link offers (directGroupLinkOffers in
    // ProductOffersTab), returning them from the API too would duplicate the
    // cards.
  ];
}

// ────────────────────────────────────────────────────────────────────────────
// BusinessInsurance — Direct Insurance external calculation API (demo)
//
// The bw-ext-direct-insurance/getCalculation endpoint is hit in three distinct
// ways by the BI wizard, all routed here:
//   1. Address page flood-zone probe — reads buildings.insObject[0].floodZoneStatus
//   2. Building reconstruction calc (getCalc:"1") — reads calc.partReconstructionValue
//   3. Quote page init (getPackages:"1") — reads packages[] (3 tiers)
//      + Confirmation submit validation — reads validations.errors (must be empty)
// We branch on the request flags and return the matching canned slice. Shapes
// mirror QuoteApiResponse in query/quoteApiTypes.ts.
// ────────────────────────────────────────────────────────────────────────────

function buildInsuranceCalculation(req: MockRequest) {
  const params = (req.body?.calculationParameters ?? {}) as Record<
    string,
    unknown
  >;

  // (2) Building reconstruction value calculator.
  if (params.getCalc === "1") {
    return {
      calc: {
        maxPartReconstructionValue: "9000000",
        umaxPartReconstructionValue: "9000000",
        partReconstructionValue: "3200000",
      },
    };
  }

  // (1) Flood-zone probe — getPackages "0" with a single selected building. Return
  // a benign flood-zone status so the address saves without underwriting.
  if (params.getPackages === "0") {
    return {
      ...insuranceCalculationBase(),
      buildings: {
        ...insuranceCalculationBase().buildings,
        insObject: [{ floodZoneStatus: "1" }],
      },
    };
  }

  // (3) Quote init — full response with the three package tiers and EMPTY
  // validations so submission isn't blocked.
  return {
    ...insuranceCalculationBase(),
    validations: { errors: [], warnings: [] },
    packages: [
      insurancePackage("1", "Essential", 3360),
      insurancePackage("2", "Smart", 4980),
      insurancePackage("3", "Premium", 7140),
    ],
  };
}

// Common scalar fields shared across all calculation responses.
function insuranceCalculationBase() {
  return {
    // EMPTY validations everywhere — neither the quote init nor the submit-time
    // calculation should be blocked in the demo.
    validations: { errors: [], warnings: [] },
    basePremiumTotal: "4980",
    premiumTotal: "4980",
    price: "4980",
    installmentDate: "2026-07-01",
    installmentValue: "4980",
    underwritingRequired: "0" as const,
    userIsUnderwriter: "0" as const,
    calculationDate: "2026-06-12",
    frequencyDescription: "Ročně",
    countBuildingsAdressPoint: "1",
    calculationOnBehalf: "0",
    calculationVersion: "1",
    minPremiumRounding: "0",
    defaultSalesDiscount: "0",
    defaultSalesDiscountPercentage: "0.15",
    payment: { reminder: "0" },
    company: { industryName: "Služby a obchod", specializationName: "Obchod" },
    buildings: {
      allInsuranceObjects: {},
      recommendation: { allInsuranceObjects: {} },
    },
    liability: { insObject: [] },
    other: { insObject: [] },
  };
}

// One package tier (PackageResult) with plausible CZK numbers.
function insurancePackage(id: string, name: string, annualPremium: number) {
  const premium = String(annualPremium);
  return {
    id,
    name,
    description: `${name} balíček pojištění podnikání`,
    price: premium,
    basePremiumTotal: premium,
    premiumTotal: premium,
    installmentDate: "2026-07-01",
    installmentValue: premium,
    calculationDate: "2026-06-12",
    calculationOnBehalf: "0",
    calculationVersion: "1",
    frequencyDescription: "Ročně",
    countBuildingsAdressPoint: "1",
    minPremiumRounding: "0",
    defaultSalesDiscount: "0",
    defaultSalesDiscountPercentage: "0.15",
    userIsUnderwriter: "0" as const,
    underwritingRequired: "0" as const,
    payment: { reminder: "0" },
    company: { industryName: "Služby a obchod", specializationName: "Obchod" },
    buildings: {
      allInsuranceObjects: {},
      recommendation: { allInsuranceObjects: {} },
      insObject: [],
    },
    liability: { insObject: [] },
    other: { insObject: [] },
  };
}

// Canned cadastral area list keyed off the entered postal code. The plot-number
// form reads cadastralArea[].{value,description,city}.
function buildCadastralArea() {
  return {
    cityDesc: "Praha",
    cityCode: "554782",
    version: "1",
    cadastralArea: [
      { value: "727164", description: "Nové Město", city: "Praha" },
      { value: "727156", description: "Staré Město", city: "Praha" },
      { value: "728039", description: "Vinohrady", city: "Praha" },
    ],
  };
}

// Canned home/property risk zone for an address postal code.
function buildHomeRiskZone() {
  return { riskZoneValue: "1", riskZoneDescription: "Nízké riziko" };
}

// Business-area industry → specializations, per the Industry/Specialization
// union in businessAreaForm.types.ts. The BI Business Area step reads these
// off the validation-rules response to build its industry/specialization
// pickers, so an empty rules object leaves both dropdowns with no options.
const BUSINESS_AREA_SPECIALIZATIONS: Record<string, string[]> = {
  FoodAndHospitality: ["FoodAndBeverage", "Accommodation"],
  ServicesAndRetail: [
    "Retail",
    "PersonalCareHealthAndEducation",
    "VehicleServices",
    "NonVehicleServices",
    "RealEstateBrokerage",
    "CourtExperts",
  ],
  ManufacturingAndWarehousing: [
    "FoodProduction",
    "MetalAndMinerals",
    "PlasticWoodPaperTextiles",
    "WarehousesForNonFlammableMaterials",
  ],
  TradesAndCrafts: [
    "ConstructionAndInstallation",
    "OtherConstructionRelatedWork",
    "OtherTrades",
  ],
};

function buildEntitiesValidationRules(req: MockRequest) {
  const documentType = (
    req.body?.documentReference as { documentType?: string }
  )?.documentType;
  if (documentType !== "ReqCreateBusinessInsuranceContract") {
    return { entitiesRules: {} };
  }
  const industries = Object.keys(BUSINESS_AREA_SPECIALIZATIONS);
  return {
    entitiesRules: {
      businessArea: {
        onEntity: {
          attrs: {
            industry: {
              type: "string",
              generalParams: { isRequired: true, allow: industries },
            },
            specialization: {
              type: "alternatives",
              generalParams: { isRequired: true },
              items: industries.map((industry) => ({
                type: "string",
                generalParams: {
                  isRequired: true,
                  allow: BUSINESS_AREA_SPECIALIZATIONS[industry],
                },
                feInfo: {
                  enumHeaderInfo: {
                    conditionalField: true,
                    dependsOn: "industry",
                    dependsOnValue: industry,
                  },
                },
              })),
            },
          },
        },
      },
    },
  };
}

function buildMyProducts() {
  // Shape must match BwContractsModel.getMyProducts response (key, productGroup,
  // price, currency, period, status, statusDays, companyName, documentId).
  // documentId drives the BusinessInsurance dashboard route — the mock serves a
  // canned LiveBusinessInsuranceContract for it, so clicking the card opens the
  // contract dashboard instead of crashing.
  return [
    {
      key: "BusinessInsurance",
      productGroup: "insurance",
      price: 4980,
      currency: "CZK",
      period: "year",
      status: "active",
      statusDays: 318,
      companyName: "Contoso Holdings AG",
      documentId: "demo-myproduct-bi-holdings",
    },
    {
      key: "FlowpayCredit",
      productGroup: "financing",
      price: 12500,
      currency: "CZK",
      period: "month",
      status: "active",
      statusDays: 12,
      companyName: "Contoso Holdings AG",
      documentId: "demo-myproduct-flowpay-holdings",
    },
  ];
}

function buildProductCatalogue() {
  return {
    documentList: [
      buildProduct(
        "primary-account",
        "Operating account",
        "PrimaryAccount",
        "Active",
      ),
      buildProduct("marketplace", "Marketplace", "Marketplace", "Active"),
      buildProduct(
        "flowpay-credit",
        "FlowPay Credit",
        "Financing",
        "Available",
      ),
      buildProduct(
        "business-insurance",
        "Business insurance",
        "Insurance",
        "Active",
      ),
      buildProduct(
        "bibby-factoring",
        "Bibby Factoring",
        "Factoring",
        "Available",
      ),
      buildProduct(
        "payment-cards",
        "Payment cards",
        "CardManagement",
        "Active",
      ),
    ],
    pagingResponse: {
      totalItemsCount: 6,
      hasNextPage: false,
      pageNumber: 1,
      pageSize: 20,
    },
  };
}

function buildProduct(
  id: string,
  name: string,
  productType: string,
  status: string,
) {
  return {
    documentHeader: { id: `demo-product-${id}`, documentType: "Product" },
    entityData: {
      productId: id,
      productName: name,
      productType,
      status,
      providerName: "Contoso Group",
    },
  };
}

function buildExchangeRates() {
  return {
    base: "EUR",
    asOf: "2026-05-21T08:00:00.000Z",
    rates: [
      { currency: "USD", buy: 1.072, sell: 1.094, mid: 1.083 },
      { currency: "GBP", buy: 0.851, sell: 0.871, mid: 0.861 },
      { currency: "PLN", buy: 4.281, sell: 4.328, mid: 4.305 },
      { currency: "CZK", buy: 24.82, sell: 25.18, mid: 25.0 },
      { currency: "CHF", buy: 0.951, sell: 0.969, mid: 0.96 },
    ],
  };
}


function buildCards() {
  const cards = [
    {
      id: "demo-card-001",
      lastFour: "4129",
      cardholder: "Anna Schmidt",
      type: "Virtual",
      currency: "EUR",
      monthlyLimit: 8_000,
      status: "Active",
    },
    {
      id: "demo-card-002",
      lastFour: "8821",
      cardholder: "Anna Schmidt",
      type: "Physical",
      currency: "EUR",
      monthlyLimit: 25_000,
      status: "Active",
    },
    {
      id: "demo-card-003",
      lastFour: "0094",
      cardholder: "Tomáš Dvořák",
      type: "Virtual",
      currency: "CZK",
      monthlyLimit: 80_000,
      status: "Active",
    },
    {
      id: "demo-card-004",
      lastFour: "5102",
      cardholder: "Mateusz Kowalski",
      type: "Virtual",
      currency: "PLN",
      monthlyLimit: 25_000,
      status: "Frozen",
    },
    {
      id: "demo-card-005",
      lastFour: "7733",
      cardholder: "Olivia Hughes",
      type: "Physical",
      currency: "GBP",
      monthlyLimit: 6_000,
      status: "Active",
    },
  ].map((c) => ({
    documentHeader: { id: c.id, documentType: "PaymentCard" },
    entityData: c,
  }));
  return {
    documentList: cards,
    pagingResponse: {
      totalItemsCount: cards.length,
      hasNextPage: false,
      pageNumber: 1,
      pageSize: 20,
    },
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Contract terms + Pricelist + fx-registers company lookup
// ────────────────────────────────────────────────────────────────────────────

function buildContractTerms() {
  return {
    entityData: {
      version: "2026.05.01",
      productType: "PrimaryAccount",
      effectiveFrom: "2026-05-01",
      attachmentUrl: "/assets/demo-terms.pdf",
      sections: [],
    },
  };
}

function buildPricelist() {
  return {
    entityData: {
      version: "2026.05.01",
      productType: "PrimaryAccount",
      effectiveFrom: "2026-05-01",
      attachmentUrl: "/assets/demo-pricelist.pdf",
      items: [],
    },
  };
}

// Returns a small CZ/AT register lookup so the Add company company-name
// autocomplete has results to render. Filters by `query` prefix when provided.
function buildCompanyLookup(req: MockRequest) {
  const entityData = (req.body?.entityData ?? {}) as {
    country?: string;
    companyNameOrRegNo?: string;
  };
  const query = String(entityData.companyNameOrRegNo ?? "").toLowerCase();

  const samples = [
    {
      registrationNumber: "27082440",
      name: "Halofield Systems AG",
      country: "AT",
      street: "Stephansplatz 8",
      city: "Vienna",
      zip: "1010",
    },
    {
      registrationNumber: "05284910",
      name: "Vexa Cloud B.V.",
      country: "NL",
      street: "Prinsengracht 482",
      city: "Amsterdam",
      zip: "1017 KG",
    },
    {
      registrationNumber: "08361729",
      name: "Atria Health s.r.o.",
      country: "CZ",
      street: "Pekařská 64",
      city: "Brno",
      zip: "602 00",
    },
    {
      registrationNumber: "07291847",
      name: "Pelegrini Hospitality Group s.r.o.",
      country: "CZ",
      street: "Vinohradská 174",
      city: "Prague",
      zip: "130 00",
    },
    {
      registrationNumber: "12918472",
      name: "Mirage Apparel Ltd.",
      country: "GB",
      street: "Deansgate 184",
      city: "Manchester",
      zip: "M3 4LY",
    },
    {
      registrationNumber: "29352886",
      name: "Northpoint Capital AG",
      country: "AT",
      street: "Schwarzenbergplatz 8",
      city: "Vienna",
      zip: "1030",
    },
    {
      registrationNumber: "60294817",
      name: "Aerial Cargo B.V.",
      country: "NL",
      street: "Wilhelminakade 22",
      city: "Rotterdam",
      zip: "3072 AP",
    },
  ];

  const filtered = query
    ? samples.filter(
        (s) =>
          s.name.toLowerCase().includes(query) ||
          s.registrationNumber.includes(query),
      )
    : samples;

  // Never dead-end the search: the samples above are fictional, so a visitor
  // typing their real company name would get "no options" and couldn't finish
  // onboarding. Synthesize a plausible company from whatever was typed so ANY
  // query (≥3 chars, the FE's search threshold) is selectable.
  const results = [...filtered];
  const raw = String(entityData.companyNameOrRegNo ?? "").trim();
  if (
    raw.length >= 3 &&
    !filtered.some((s) => s.name.toLowerCase() === query)
  ) {
    results.push(synthesizeLookupCompany(raw, entityData.country));
  }

  return {
    returnedItems: results.length,
    entityList: results.map((s) => ({
      companyBasicInfo: { companyName: s.name },
      companyIdentifier: {
        partyIdentifierType: "Ico",
        partyIdentifierValue: s.registrationNumber,
      },
      companyAddress: {
        street: s.street,
        city: s.city,
        zip: s.zip,
        country: s.country,
      },
      registryReference: {
        registry: "Cor",
        registryCountry: entityData.country ?? s.country,
        registryId: s.registrationNumber,
      },
    })),
  };
}

// Registered seat for synthesized lookup results, keyed by registry country
// (both alpha-2 and alpha-3 — the lookup form sends "CZE"-style codes).
const SYNTH_SEAT_DEFAULT = {
  street: "Václavské náměstí 846/1",
  city: "Praha",
  zip: "110 00",
};
const SYNTH_SEAT: Record<string, typeof SYNTH_SEAT_DEFAULT> = {
  CZ: SYNTH_SEAT_DEFAULT,
  CZE: SYNTH_SEAT_DEFAULT,
  SK: { street: "Hlavné námestie 5", city: "Bratislava", zip: "811 01" },
  SVK: { street: "Hlavné námestie 5", city: "Bratislava", zip: "811 01" },
  AT: { street: "Kärntner Straße 12", city: "Wien", zip: "1010" },
  AUT: { street: "Kärntner Straße 12", city: "Wien", zip: "1010" },
  DE: { street: "Unter den Linden 21", city: "Berlin", zip: "10115" },
  DEU: { street: "Unter den Linden 21", city: "Berlin", zip: "10115" },
  GB: { street: "1 King Street", city: "London", zip: "EC1A 1AA" },
  GBR: { street: "1 King Street", city: "London", zip: "EC1A 1AA" },
  PL: { street: "Nowy Świat 6", city: "Warszawa", zip: "00-001" },
  POL: { street: "Nowy Świat 6", city: "Warszawa", zip: "00-001" },
  NL: { street: "Damrak 70", city: "Amsterdam", zip: "1012 AB" },
  NLD: { street: "Damrak 70", city: "Amsterdam", zip: "1012 AB" },
};

// Build a believable registry entry from a free-text query: append a legal
// form when missing and derive a deterministic 8-digit reg. number from the
// text (numeric queries pass through as the reg. number itself).
function synthesizeLookupCompany(raw: string, country: string | undefined) {
  const digitsOnly = /^\d+$/.test(raw);
  const hasLegalForm =
    /(s\.r\.o\.?|a\.s\.?|k\.s\.?|gmbh|ag|se|ltd\.?|b\.v\.?|sp\. z o\.o\.?|inc\.?|s\.a\.?)\s*$/i.test(
      raw,
    );
  const name = digitsOnly
    ? `Společnost ${raw} s.r.o.`
    : hasLegalForm
      ? raw
      : `${raw} s.r.o.`;

  let hash = 2166136261;
  for (let i = 0; i < raw.length; i++) {
    hash ^= raw.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  const registrationNumber = digitsOnly
    ? raw.padStart(8, "0").slice(0, 8)
    : String(10000000 + ((hash >>> 0) % 89999999));

  const seat = SYNTH_SEAT[(country ?? "").toUpperCase()] ?? SYNTH_SEAT_DEFAULT;
  return {
    registrationNumber,
    name,
    country: country ?? "CZ",
    ...seat,
  };
}
